import { Manifest, RecentObservedData } from '../types';
import type { AtmosphericColumn, InversionMetrics, SoundingLevelData } from '../types';
import store from '../state';
import * as pmtiles from 'pmtiles';
import * as SunCalc from 'suncalc';
import { getTileUrl, resolvePmtilesTileUrl, buildSampleTileUrl, appendAuthToken, getApiServer, getOrCreatePMTiles } from '../config';
import tzlookup from 'tz-lookup';
import { ForecastBinaryService, DecodedPointForecast, seeingScoreToArcsec } from './ForecastBinaryService';
import { ObservedBinaryService } from './ObservedBinaryService';
import TierAccessService, { TileAccessDenial } from './TierAccessService';
import ManifestTimelineHelper from './ManifestTimelineHelper';
import { fetchWindGrid } from '../utils/WindGridLoader';
import TilePixelSampler from './TilePixelSampler';

export interface ForecastLoadRequirements {
  needsSolar?: boolean;
  needsStargazing?: boolean;
  needsMountains?: boolean;
  needsObserved?: boolean;
}

function getAuroraTargetDate(d: Date): string {
  const year = d.getFullYear();
  const month = d.getMonth();
  const date = d.getDate();
  const hours = d.getHours();
  
  const targetDate = new Date(year, month, date);
  if (hours < 7) {
    targetDate.setDate(targetDate.getDate() - 1);
  }
  
  const yyyy = targetDate.getFullYear();
  const mm = String(targetDate.getMonth() + 1).padStart(2, '0');
  const dd = String(targetDate.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function getElevationPMTiles(): pmtiles.PMTiles {
  const state = store.getState();
  const manifest = state.manifest;

  const apiServer = getApiServer(manifest);
  const tileRoot = (manifest ? manifest.tile_base : "tiles").replace(/^\/+|\/+$/g, '');

  const cleanServer = apiServer.replace(/\/$/, '');
  let cleanPath = `${tileRoot}/basemaps/Elevation_merged_0_10.pmtiles`.replace(/\/+/g, '/');
  if (cleanPath.startsWith('/')) {
    cleanPath = cleanPath.substring(1);
  }

  const url = appendAuthToken(`${cleanServer}/${cleanPath}`);
  return getOrCreatePMTiles(url);
}

export function getBortlePMTiles(): pmtiles.PMTiles {
  const state = store.getState();
  const manifest = state.manifest;

  const apiServer = getApiServer(manifest);
  const tileRoot = (manifest ? manifest.tile_base : "tiles").replace(/^\/+|\/+$/g, '');

  const cleanServer = apiServer.replace(/\/$/, '');
  let cleanPath = `${tileRoot}/basemaps/bortle_baseline_0_4.pmtiles`.replace(/\/+/g, '/');
  if (cleanPath.startsWith('/')) {
    cleanPath = cleanPath.substring(1);
  }

  const url = appendAuthToken(`${cleanServer}/${cleanPath}`);
  return getOrCreatePMTiles(url);
}

export interface ElevationProfile {
  elevation: number;
  minElevation: number;
  maxElevation: number;
}

export async function getElevationAt(lat: number, lon: number, probeZoom: number = 10): Promise<ElevationProfile> {
  if (!TierAccessService.userHasLayerAccess('basemap_elevation_dem')) {
    return { elevation: 0, minElevation: 0, maxElevation: 0 };
  }
  let zoom = probeZoom;
  let response = null;
  let tx = 0;
  let ty = 0;

  try {
    const pmtilesInstance = getElevationPMTiles();
    while (zoom >= 0) {
      tx = lng2tile(lon, zoom);
      ty = lat2tile(lat, zoom);
      try {
        response = await pmtilesInstance.getZxy(zoom, tx, ty);
        if (response) {
          break;
        }
      } catch (e) {
        // ignore and try lower zoom
      }
      zoom--;
    }

    if (!response) return { elevation: 0, minElevation: 0, maxElevation: 0 };

    const blob = new Blob([response.data], { type: 'image/png' });
    const url = URL.createObjectURL(blob);

    return new Promise((resolve) => {
      const safetyTimeout = setTimeout(() => {
        URL.revokeObjectURL(url);
        resolve({ elevation: 0, minElevation: 0, maxElevation: 0 });
      }, 3000);

      const img = new Image();
      img.onload = () => {
        clearTimeout(safetyTimeout);
        URL.revokeObjectURL(url);
        const w = img.width || 512;
        const h = img.height || 512;
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          resolve({ elevation: 0, minElevation: 0, maxElevation: 0 });
          return;
        }

        const worldDim = Math.pow(2, zoom) * w;
        const pixelX = Math.floor(((lon + 180) / 360) * worldDim) % w;
        const sinLat = Math.sin(lat * Math.PI / 180);
        const pixelY = Math.floor((0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * worldDim) % h;

        ctx.drawImage(img, 0, 0, w, h);
        const pData = ctx.getImageData(pixelX, pixelY, 1, 1).data;
        const r = pData[0], g = pData[1], b = pData[2];
        const elevMeters = (r * 256 + g + b / 256) - 32768;
        const elevFeet = Math.round(elevMeters * 3.28084);

        // Neighborhood search (radius scaled to maintain a constant physical coverage box)
        const radius = Math.max(1, Math.round(25 * Math.pow(2, zoom - 8)));
        const x0 = Math.max(0, pixelX - radius);
        const y0 = Math.max(0, pixelY - radius);
        const x1 = Math.min(w - 1, pixelX + radius);
        const y1 = Math.min(h - 1, pixelY + radius);
        
        const imgData = ctx.getImageData(x0, y0, x1 - x0 + 1, y1 - y0 + 1);
        let minElev = Infinity;
        let maxElev = -Infinity;

        for (let i = 0; i < imgData.data.length; i += 4) {
          const pr = imgData.data[i];
          const pg = imgData.data[i+1];
          const pb = imgData.data[i+2];
          const pa = imgData.data[i+3];
          if (pa === 0) continue;

          const m = (pr * 256 + pg + pb / 256) - 32768;
          const ft = m * 3.28084;
          if (ft < minElev) minElev = ft;
          if (ft > maxElev) maxElev = ft;
        }

        resolve({
          elevation: elevFeet,
          minElevation: minElev === Infinity ? elevFeet : Math.round(minElev),
          maxElevation: maxElev === -Infinity ? elevFeet : Math.round(maxElev)
        });
      };
      img.onerror = () => {
        clearTimeout(safetyTimeout);
        URL.revokeObjectURL(url);
        resolve({ elevation: 0, minElevation: 0, maxElevation: 0 });
      };
      img.src = url;
    });
  } catch (e) {
    console.error("Error querying elevation PMTiles:", e);
    return { elevation: 0, minElevation: 0, maxElevation: 0 };
  }
}

export async function getBaseElevationAt(lat: number, lon: number): Promise<number> {
  const profile = await getElevationAt(lat, lon, 4);
  return profile.elevation;
}


// Reusing probing logic
function lng2tile(lon: number, zoom: number): number {
  return Math.floor((lon + 180) / 360 * Math.pow(2, zoom));
}
function lat2tile(lat: number, zoom: number): number {
  return Math.floor((1 - Math.log(Math.tan(lat * Math.PI / 180) + 1 / Math.cos(lat * Math.PI / 180)) / Math.PI) / 2 * Math.pow(2, zoom));
}

export async function getGenericScoreAt(lat: number, lon: number, tileUrl: string): Promise<number> {
  const p = await getPixelAt(lat, lon, tileUrl);
  if (!p || (p[3] === 0 && p[0] === 0 && p[1] === 0 && p[2] === 0)) return 0;
  
  const r = p[0];
  const g = p[1];
  const b = p[2];
  const a = p[3];

  if (a === 0 && r === 0 && g === 0 && b === 0) return 0;

  // Cool zone (scores 0-49): Blue is present (b > 2) or green dominates red while b > 0
  // Generated by apply_blend50_gradient: r = 255*t, g = 70+185*t, b = 160*(1-t), where t = score / 50.0
  if (b > 2 || (g > r && b > 0)) {
    const tb = 1.0 - Math.min(160, b) / 160.0;
    const tr = r / 255.0;
    const t = (tb + tr) / 2.0;
    const score = t * 50.0;
    return Math.min(49, Math.max(0, Math.round(score)));
  }

  // Warm zone (scores 50-100): b is 0 or near 0
  // Scores 50-94: Yellow -> Orange -> Red (g goes from 255 down to > 5)
  if (g > 5) {
    const t = 1.0 - (g / 255.0);
    const score = 50.0 + t * 45.0;
    return Math.min(94, Math.max(50, Math.round(score)));
  }

  // Scores 95-100: Pure Red (r=255, g<=5, b<=2)
  // Alpha was mapped from 227 (at score 95) up to 235 (at score 100)
  if (a > 227) {
    const extra = ((a - 227) / (235 - 227)) * 5.0;
    return Math.min(100, Math.max(95, Math.round(95.0 + extra)));
  }
  return 95;
}

export async function getDarkskiesScoreAt(lat: number, lon: number, tileUrl: string): Promise<number> {
  const p = await getPixelAt(lat, lon, tileUrl);
  if (!p || p[3] === 0) return 0;
  const r = p[0], g = p[1], b = p[2];
  let score = 0;

  if (b > 200 && r < 30) {
    score = 8.0 + Math.min(1.0, Math.max(0.0, (255 - g) / 205.0)) * 2.0;
  } else if (g > 200 && r < 30) {
    score = 6.0 + Math.min(1.0, Math.max(0.0, b / 255.0)) * 2.0;
  } else if (g > 200 && b < 30) {
    score = 4.0 + Math.min(1.0, Math.max(0.0, (255 - r) / 255.0)) * 2.0;
  } else if (r > 200 && b < 30 && g >= 120) {
    score = 2.0 + Math.min(1.0, Math.max(0.0, (g - 128.0) / 127.0)) * 2.0;
  } else if (r >= 150 && b < 30) {
    score = Math.min(1.0, Math.max(0.0, g / 128.0)) * 2.0;
  }
  return Math.min(10, Math.max(0, Math.round(score * 10) / 10));
}

export async function getBortleAt(lat: number, lon: number, probeZoom: number = 4): Promise<number> {
  if (!TierAccessService.userHasLayerAccess('basemap_bortle_light_pollution')) {
    return 4;
  }
  let zoom = probeZoom;
  let response = null;
  let tx = 0;
  let ty = 0;

  try {
    const pmtilesInstance = getBortlePMTiles();
    while (zoom >= 0) {
      tx = lng2tile(lon, zoom);
      ty = lat2tile(lat, zoom);
      try {
        response = await pmtilesInstance.getZxy(zoom, tx, ty);
        if (response) {
          break;
        }
      } catch (e) {
        // ignore and try lower zoom
      }
      zoom--;
    }

    if (!response) return 4;

    const blob = new Blob([response.data], { type: 'image/png' });
    const url = URL.createObjectURL(blob);

    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const w = img.width || 256;
        const h = img.height || 256;
        const canvas = document.createElement('canvas');
        canvas.width = w; canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        if (!ctx) {
          resolve(4);
          return;
        }

        const worldDim = Math.pow(2, zoom) * w;
        const pixelX = Math.floor(((lon + 180) / 360) * worldDim) % w;
        const sinLat = Math.sin(lat * Math.PI / 180);
        const pixelY = Math.floor((0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * worldDim) % h;

        ctx.drawImage(img, 0, 0, w, h);
        const pData = ctx.getImageData(pixelX, pixelY, 1, 1).data;
        const alpha = pData[3];
        const calculatedBortle = Math.round(alpha / 25.0);
        if (calculatedBortle >= 1 && calculatedBortle <= 9) {
          resolve(calculatedBortle);
        } else {
          resolve(4);
        }
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        resolve(4);
      };
      img.src = url;
    });
  } catch (e) {
    console.error("Error querying Bortle PMTiles:", e);
    return 4;
  }
}

function getMoonPhaseDetails(date: Date) {
  const m = SunCalc.getMoonIllumination(date);
  let b = Math.round(m.phase * 8);
  if (b >= 8) b = 0;
  return {
    name: ["New Moon", "Waxing Crescent", "First Quarter", "Waxing Gibbous", "Full Moon", "Waning Gibbous", "Last Quarter", "Waning Crescent"][b],
    icon: ["🌑", "🌒", "🌓", "🌔", "🌕", "🌖", "🌗", "🌘"][b],
    illumination: Math.round(m.fraction * 100) + "% Illumination"
  };
}

export interface DetailedForecastData {
  timeLabels: string[];
  timelineStartMs?: number;
  hasBinaryPointData?: boolean;
  accessDenial?: TileAccessDenial | null;
  darkskiesScores?: (number | null)[];
  seeingScores?: (number | null)[];
  seeingArcsec?: (number | null)[];
  dewRiskHourly?: number[];
  tempsFlat: number[];
  tempsMountain: number[];
  tempsMid: number[];
  tempsForecast: number[];
  tempsHighest: number[];
  tempsLowest: number[];
  tempsPoi?: number[];
  precipLikelihood: number[];
  precipRate: number[];
  snowRate?: number[];
  cloudCover: number[];
  smokeDensity: number[];
  aqiValues: number[];
  windSpeed: number[];
  windDir: number[];
  lightningData: any; // GeoJSON mapping
  thunderstormProb: number[];
  recentObserved?: RecentObservedData | null;
  
  // Elevations
  poiElev: number;
  baseElev: number;
  forecastElev: number;
  minElev: number;
  maxElev: number;
  avgFreezingElev: number;
  freezingLevels: number[];

  // Solar & Stargazing
  astroDawnTime: string;
  astroDuskTime: string;
  darkskiesScore: number;
  
  // Primary day moon details
  moonIcon: string;
  moonName: string;
  moonIllumination: string;
  moonRiseTime: string;
  moonSetTime: string;
  
  opticsDewRisk?: {
    level: string;
    badge: string;
    score: number;
  };

  // Sun Intensity Forecast (W/m²)
  maxSunIntensity: number;
  sunIntensityCategory: string;
  sunIntensityHourly: number[];

  // Inversion & Atmospheric Soundings
  soundings?: AtmosphericColumn[];
  inversionMetrics?: InversionMetrics[];
  
  moonPhaseOutlook: { date: string, name: string, icon: string, rise: string, set: string }[];
  solarOutlook: { 
    date: string, 
    sunriseTime: string, 
    sunsetTime: string, 
    sunriseScore: number, 
    sunsetScore: number, 
    alpenglowMorn: number, 
    alpenglowEve: number,
    goldenHourMornScore?: number,
    goldenHourEveScore?: number,
    goldenHourMorn: string,
    goldenHourEve: string,
    blueHourMorn: string,
    blueHourEve: string
  }[];
  darkskiesTrends: {
    date: string;
    dateLabel: string;
    labels: string[];
    scores: number[];
    kpLabels: string[];
    kpValues: number[];
    auroraProb: number;
    isAuroraAvailable: boolean;
    auroraDateStr: string;
    moonIcon: string;
    moonName: string;
    moonIllumination: string;
    moonRiseTime: string;
    moonSetTime: string;
    astroDawnTime: string;
    astroDuskTime: string;
    darkskiesScore: number;
  }[];
  spaceWeather?: {
    currentKp: number;
    tonightMaxKp: number;
    tomorrowMaxKp: number;
    solarWindSpeedKms: number | null;
    solarWindDensityCm3: number | null;
    bzNt: number | null;
    btNt: number | null;
    hpiNorthGw: number | null;
    hpiSouthGw: number | null;
    propagatedTimeTag: string | null;
    updatedAt: string | null;
  };
  timezone: string;
  localDate: string;
  localTime: string;
  timezoneAbbr: string;
}

export interface ModelExtent {
  latMin: number;
  latMax: number;
  lonMin: number;
  lonMax: number;
}

export const MODEL_EXTENTS: Record<string, ModelExtent> = {
  hrrr: { latMin: 24.0, latMax: 50.0, lonMin: -125.0, lonMax: -66.0 },
  rrfs: { latMin: 24.0, latMax: 50.0, lonMin: -125.0, lonMax: -66.0 },
  rdps: { latMin: 40.0, latMax: 84.0, lonMin: -141.0, lonMax: -47.0 },
  blend: { latMin: 24.0, latMax: 50.0, lonMin: -125.0, lonMax: -66.0 },
};

export function isPointInUS(lat: number, lon: number): boolean {
  // Conterminous US
  if (lat >= 24.0 && lat <= 50.0 && lon >= -125.0 && lon <= -66.0) return true;
  // Alaska
  if (lat >= 50.0 && lat <= 72.0 && lon >= -180.0 && lon <= -130.0) return true;
  // Hawaii
  if (lat >= 18.0 && lat <= 23.0 && lon >= -161.0 && lon <= -154.0) return true;
  return false;
}

export function isPointInCanada(lat: number, lon: number): boolean {
  return lat >= 41.0 && lat <= 84.0 && lon >= -141.0 && lon <= -47.0;
}

export function isModelAvailableAt(model: string, lat: number, lon: number): boolean {
  if (model === 'gfs' || model === 'ecmwf' || model === 'gdps' || model === 'icon13') return true;
  const ext = MODEL_EXTENTS[model];
  if (!ext) return true;
  return lat >= ext.latMin && lat <= ext.latMax && lon >= ext.lonMin && lon <= ext.lonMax;
}

async function fetchWithTimeout(url: string, options: RequestInit = {}, timeoutMs = 8000): Promise<Response> {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
}

async function fetchWithUserAgent(url: string, timeoutMs = 8000): Promise<any> {
  const headers = new Headers();
  headers.set('User-Agent', 'SkySkoutWeatherApp/2.0 (contact@cascadiasoft.com)');
  const resp = await fetchWithTimeout(url, { headers }, timeoutMs);
  if (!resp.ok) {
    throw new Error(`API error: HTTP ${resp.status}`);
  }
  return await resp.json();
}

function getCachedEcccGrid(lat: number, lon: number): any | null {
  const key = `eccc_grid_${lat.toFixed(4)}_${lon.toFixed(4)}`;
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return null;
    const item = JSON.parse(cached);
    const now = Date.now();
    if (now < item.expiresAt) {
      return item.data;
    } else {
      sessionStorage.removeItem(key);
    }
  } catch (e) {
    // ignore
  }
  return null;
}

function cacheEcccGrid(lat: number, lon: number, data: any) {
  const key = `eccc_grid_${lat.toFixed(4)}_${lon.toFixed(4)}`;
  const item = {
    data,
    expiresAt: Date.now() + 10 * 60 * 1000 // 10 minutes
  };
  try {
    sessionStorage.setItem(key, JSON.stringify(item));
  } catch (e) {
    // ignore
  }
}

async function fetchEcccGridData(lat: number, lon: number): Promise<any> {
  const cached = getCachedEcccGrid(lat, lon);
  if (cached) return cached;
  
  let url = `https://api.weather.gc.ca/collections/citypageweather-realtime/items?bbox=${lon - 1.0},${lat - 1.0},${lon + 1.0},${lat + 1.0}&f=json`;
  let data = await fetchWithUserAgent(url);
  
  if (!data?.features || data.features.length === 0) {
    url = `https://api.weather.gc.ca/collections/citypageweather-realtime/items?bbox=${lon - 3.0},${lat - 3.0},${lon + 3.0},${lat + 3.0}&f=json`;
    data = await fetchWithUserAgent(url);
  }

  if (!data?.features || data.features.length === 0) {
    throw new Error('No ECCC weather stations found in bounding box');
  }

  let closestFeature = data.features[0];
  let minDistance = Infinity;
  for (const feat of data.features) {
    if (!feat.geometry?.coordinates) continue;
    const [cLon, cLat] = feat.geometry.coordinates;
    const dist = Math.pow(cLon - lon, 2) + Math.pow(cLat - lat, 2);
    if (dist < minDistance) {
      minDistance = dist;
      closestFeature = feat;
    }
  }

  const result = closestFeature.properties;
  cacheEcccGrid(lat, lon, result);
  return result;
}

function lookupEcccHourlyForecast(hForecasts: any[], targetUtcMs: number): any | null {
  if (!Array.isArray(hForecasts)) return null;
  let bestItem = null;
  let minDiff = 1800 * 1000;
  for (const item of hForecasts) {
    if (!item.timestamp) continue;
    const itemMs = Date.parse(item.timestamp);
    if (isNaN(itemMs)) continue;
    const diff = Math.abs(itemMs - targetUtcMs);
    if (diff < minDiff) {
      minDiff = diff;
      bestItem = item;
    }
  }
  return bestItem;
}

function estimateCloudCoverFromCondition(condition: string): number {
  const cond = condition.toLowerCase();
  if (cond.includes('overcast') || cond.includes('cloudy') || cond.includes('couvert')) return 90;
  if (cond.includes('mainly cloudy') || cond.includes('plupart nuageux')) return 75;
  if (cond.includes('mix') || cond.includes('partly') || cond.includes('alternance')) return 50;
  if (cond.includes('mainly sunny') || cond.includes('généralement ensoleillé')) return 25;
  if (cond.includes('sunny') || cond.includes('clear') || cond.includes('ensoleillé') || cond.includes('dégagé')) return 5;
  return 50;
}

function compassToDegrees(dir: string): number {
  const mapping: Record<string, number> = {
    'N': 0, 'NNE': 22.5, 'NE': 45, 'ENE': 67.5,
    'E': 90, 'ESE': 112.5, 'SE': 135, 'SSE': 157.5,
    'S': 180, 'SSW': 202.5, 'SW': 225, 'WSW': 247.5,
    'W': 270, 'WNW': 292.5, 'NW': 315, 'NNW': 337.5
  };
  return mapping[dir.toUpperCase()] ?? 0;
}

function getCachedNwsGrid(lat: number, lon: number): any | null {
  const key = `nws_grid_${lat.toFixed(4)}_${lon.toFixed(4)}`;
  try {
    const cached = sessionStorage.getItem(key);
    if (!cached) return null;
    const item = JSON.parse(cached);
    const now = Date.now();
    if (now < item.expiresAt) {
      return item.data;
    } else {
      sessionStorage.removeItem(key);
    }
  } catch (e) {
    // ignore
  }
  return null;
}

function cacheNwsGrid(lat: number, lon: number, data: any) {
  const key = `nws_grid_${lat.toFixed(4)}_${lon.toFixed(4)}`;
  const item = {
    data,
    expiresAt: Date.now() + 10 * 60 * 1000 // 10 minutes
  };
  try {
    sessionStorage.setItem(key, JSON.stringify(item));
  } catch (e) {
    // ignore
  }
}

async function fetchNwsGridData(lat: number, lon: number): Promise<any> {
  const cached = getCachedNwsGrid(lat, lon);
  if (cached) return cached;
  const pointsUrl = `https://api.weather.gov/points/${lat.toFixed(4)},${lon.toFixed(4)}`;
  const pointsData = await fetchWithUserAgent(pointsUrl);
  const gridUrl = pointsData?.properties?.forecastGridData;
  if (!gridUrl) {
    throw new Error('NWS grid data URL not found in points response');
  }
  const gridData = await fetchWithUserAgent(gridUrl);
  if (gridData && gridData.properties && pointsData?.properties?.elevation) {
    gridData.properties.elevation = pointsData.properties.elevation;
  }
  cacheNwsGrid(lat, lon, gridData);
  return gridData;
}

function parseNwsInterval(validTimeStr: string): { start: number, end: number } | null {
  const parts = validTimeStr.split('/');
  if (parts.length !== 2) return null;
  const startMs = Date.parse(parts[0]);
  if (isNaN(startMs)) return null;
  
  const durationStr = parts[1];
  let durationMs = 0;
  
  const matchHours = durationStr.match(/PT(\d+)H/);
  if (matchHours) {
    durationMs = parseInt(matchHours[1]) * 60 * 60 * 1000;
  } else {
    const matchDays = durationStr.match(/P(\d+)D/);
    if (matchDays) {
      durationMs = parseInt(matchDays[1]) * 24 * 60 * 60 * 1000;
    } else {
      const matchMin = durationStr.match(/PT(\d+)M/);
      if (matchMin) {
        durationMs = parseInt(matchMin[1]) * 60 * 1000;
      } else {
        durationMs = 60 * 60 * 1000;
      }
    }
  }
  return { start: startMs, end: startMs + durationMs };
}

function lookupNwsGridValue(property: any, targetUtcMs: number): number | null {
  if (!property || !Array.isArray(property.values)) return null;
  for (const item of property.values) {
    const interval = parseNwsInterval(item.validTime);
    if (interval && targetUtcMs >= interval.start && targetUtcMs < interval.end) {
      return item.value;
    }
  }
  return null;
}

async function getPixelAt(lat: number, lon: number, tileUrl: string): Promise<[number, number, number, number] | null> {
  const probeZoom = 4;
  const cleanPath = (tileUrl || '').split('?')[0];
  if (cleanPath.endsWith('.webp') && !cleanPath.includes('{z}') && !/\/\d+\/\d+\/\d+\.webp$/i.test(cleanPath)) {
    const p = await TilePixelSampler.getInstance().samplePixel(tileUrl, lon, lat, probeZoom);
    if (!p) return null;
    return [p.r, p.g, p.b, p.a];
  }
  const tx = lng2tile(lon, probeZoom);
  const ty = lat2tile(lat, probeZoom);
  const sampleUrl = buildSampleTileUrl(tileUrl, `${probeZoom}/${tx}/${ty}`);
  const resolvedUrl = await resolvePmtilesTileUrl(sampleUrl);
  if (!resolvedUrl || resolvedUrl.startsWith('pmtiles://') || (!resolvedUrl.startsWith('blob:') && !resolvedUrl.startsWith('http://') && !resolvedUrl.startsWith('https://'))) {
    return null;
  }
  const isBlob = resolvedUrl.startsWith('blob:');

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      if (isBlob) URL.revokeObjectURL(resolvedUrl);
      const canvas = document.createElement('canvas');
      canvas.width = 256; canvas.height = 256;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      if (!ctx) return resolve(null);

      const worldDim = Math.pow(2, probeZoom) * 256;
      const pixelX = Math.floor(((lon + 180) / 360) * worldDim) % 256;
      const sinLat = Math.sin(lat * Math.PI / 180);
      const pixelY = Math.floor((0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * worldDim) % 256;

      ctx.drawImage(img, 0, 0);
      const p = ctx.getImageData(pixelX, pixelY, 1, 1).data;
      resolve([p[0], p[1], p[2], p[3]]);
    };
    img.onerror = () => {
      if (isBlob) URL.revokeObjectURL(resolvedUrl);
      resolve(null);
    };
    img.src = resolvedUrl;
  });
}

function decodeTemp(r: number, g: number, b: number): number {
  let temp = -20.0;
  const maxVal = Math.max(r, g, b);
  if (maxVal >= 30) {
    if (r >= g && r >= b) {
      if (g < 15 && b < 15) {
        temp = 40.0;
      } else if (g <= 135) {
        temp = 30.0 + 10.0 * (1.0 - g / 128.0);
      } else {
        temp = 20.0 + 10.0 * ((255.0 - g) / 127.0);
      }
    } else if (g >= r && g >= b) {
      if (r >= b) {
        temp = 10.0 + 10.0 * (r / 255.0);
      } else {
        temp = 10.0 * (1.0 - b / 255.0);
      }
    } else {
      if (g >= r) {
        temp = -10.0 + 10.0 * (g / 255.0);
      } else {
        temp = -20.0 + 10.0 * (1.0 - r / 255.0);
      }
    }
  }
  return temp;
}

function findNearestHour(availableHours: string[], targetHourStr: string, maxDiffHours = 3): string | null {
  if (!availableHours || availableHours.length === 0) return null;
  if (availableHours.includes(targetHourStr)) return targetHourStr;

  const targetH = parseInt(targetHourStr, 10);
  if (isNaN(targetH)) return null;

  let bestHour: string | null = null;
  let minDiff = Infinity;

  for (const hStr of availableHours) {
    const hVal = parseInt(hStr.replace(/\D/g, ''), 10);
    if (isNaN(hVal)) continue;
    const diff = Math.abs(hVal - targetH);
    if (diff < minDiff) {
      minDiff = diff;
      bestHour = hStr;
    }
  }

  if (minDiff <= maxDiffHours) {
    return bestHour;
  }
  return null;
}

async function getTempForModelAt(lat: number, lon: number, model: string, date: string, hour: string, manifest: Manifest): Promise<number | null> {
  const hrtempConfig = manifest.hrtemp?.available_by_model || manifest.hrtemp?.available || {};

  if (model === 'blend' && !hrtempConfig['blend']) {
    const modelsToTry = manifest.exposed_models?.hrtemp || ['ecmwf', 'gfs', 'hrrr', 'rdps', 'rrfs'];
    const results: number[] = [];
    for (const m of modelsToTry) {
      if (m === 'blend') continue;
      const val = await getTempForModelAt(lat, lon, m, date, hour, manifest);
      if (val !== null) results.push(val);
    }
    if (results.length > 0) {
      return results.reduce((a, b) => a + b, 0) / results.length;
    }
    return null;
  }

  const modelData = hrtempConfig[model] || {};
  const hours = modelData[date] || [];
  const matchedHour = findNearestHour(hours, hour);
  if (!matchedHour) return null;

  const tileCoords = `4/${lng2tile(lon, 4)}/${lat2tile(lat, 4)}`;
  const tileUrl = buildSampleTileUrl(getTileUrl(manifest, 'hrtemp', model, date, matchedHour), tileCoords);
  const pixel = await getPixelAt(lat, lon, tileUrl);
  if (pixel && pixel[3] > 0) {
    return decodeTemp(pixel[0], pixel[1], pixel[2]);
  }
  return null;
}

async function getCloudCoverAt(lat: number, lon: number, model: string, date: string, hour: string, manifest: Manifest): Promise<number | null> {
  const cloudConfig = manifest.clouds?.available_by_model || manifest.clouds?.available || {};

  if (model === 'blend' && !cloudConfig['blend']) {
    const modelsToTry = manifest.exposed_models?.clouds || ['ecmwf', 'gfs', 'hrrr', 'rdps', 'rrfs'];
    const results: number[] = [];
    for (const m of modelsToTry) {
      if (m === 'blend') continue;
      const val = await getCloudCoverAt(lat, lon, m, date, hour, manifest);
      if (val !== null) results.push(val);
    }
    if (results.length > 0) {
      return Math.round(results.reduce((a, b) => a + b, 0) / results.length);
    }
    return null;
  }

  const modelData = cloudConfig[model] || {};
  const tileCoords = `4/${lng2tile(lon, 4)}/${lat2tile(lat, 4)}`;

  if (modelData['total']) {
    const totalTimes: string[] = modelData['total'];
    const matchingTimes = totalTimes.filter(t => t.startsWith(date));
    const hoursInDate = matchingTimes.map(t => {
      const parts = t.split('_');
      return (parts[1] || '').replace(/UTC$/i, '');
    });
    const matchedHourStr = findNearestHour(hoursInDate, hour);

    if (matchedHourStr) {
      const targetTimeStr = matchingTimes.find(t => t.includes(`_${matchedHourStr}`)) || `${date}_${matchedHourStr}UTC`;
      const tileUrl = buildSampleTileUrl(getTileUrl(manifest, 'clouds', model, date, `total_${targetTimeStr}`), tileCoords);
      const pixel = await getPixelAt(lat, lon, tileUrl);
      if (pixel && pixel[3] > 0) {
        return Math.round((pixel[0] / 200) * 100);
      }
    }
  }

  const levels = ['low', 'mid', 'high'];
  const promises = levels.map(async (level) => {
    if (modelData[level]) {
      const times: string[] = modelData[level];
      const matchingTimes = times.filter(t => t.startsWith(date));
      const hoursInDate = matchingTimes.map(t => {
        const parts = t.split('_');
        return (parts[1] || '').replace(/UTC$/i, '');
      });
      const matchedHourStr = findNearestHour(hoursInDate, hour);
      if (matchedHourStr) {
        const targetTimeStr = matchingTimes.find(t => t.includes(`_${matchedHourStr}`)) || `${date}_${matchedHourStr}UTC`;
        const tileUrl = buildSampleTileUrl(getTileUrl(manifest, 'clouds', model, date, `${level}_${targetTimeStr}`), tileCoords);
        const pixel = await getPixelAt(lat, lon, tileUrl);
        if (pixel && pixel[3] > 0) {
          if (level === 'low') return Math.round((pixel[2] / 230) * 100);
          if (level === 'mid') return Math.round((pixel[1] / 180) * 100);
          if (level === 'high') return Math.round((pixel[0] / 210) * 100);
        }
      }
    }
    return 0;
  });

  const values = await Promise.all(promises);
  const maxVal = Math.max(...values);
  return maxVal > 0 ? maxVal : null;
}

async function getPrecipLikelihoodAt(lat: number, lon: number, model: string, date: string, hour: string, manifest: Manifest): Promise<number | null> {
  const precipConfig = manifest.precip?.available_by_model || manifest.precip?.available || {};

  if (model === 'blend' || !precipConfig[model]) {
    const modelsToTry = manifest.exposed_models?.precip || Object.keys(precipConfig);
    const validModels = modelsToTry.filter(m => m !== 'blend' && precipConfig[m]);
    if (validModels.length === 0) return 0;

    const results: number[] = [];
    for (const m of validModels) {
      const val = await getPrecipLikelihoodAt(lat, lon, m, date, hour, manifest);
      if (val !== null) results.push(val);
    }
    if (results.length > 0) {
      return Math.round(results.reduce((a, b) => a + b, 0) / results.length);
    }
    return 0;
  }

  const modelData = precipConfig[model] || {};
  const hours = modelData[date] || [];
  const matchedHour = findNearestHour(hours, hour);
  if (!matchedHour) return 0;

  const tileCoords = `4/${lng2tile(lon, 4)}/${lat2tile(lat, 4)}`;
  const tileUrl = buildSampleTileUrl(getTileUrl(manifest, 'precip', model, date, matchedHour), tileCoords);
  const pixel = await getPixelAt(lat, lon, tileUrl);
  if (pixel && pixel[3] > 0) {
    const [r, g, b] = pixel;
    if (r === 96 && g === 165 && b === 250) return 30;
    if (r === 37 && g === 99 && b === 235) return 65;
    if (r === 147 && g === 51 && b === 234) return 90;
  }
  return 0;
}

const windGridCache = new Map<string, any>();

async function getWindFromGrid(lat: number, lon: number, model: string, dateStr: string, hourStr: string, manifest: Manifest): Promise<{ speed: number, dir: number } | null> {
  const windConfig = manifest.winds?.available_by_model || manifest.winds?.available || {};

  if (model === 'blend' && !windConfig['blend']) {
    const modelsToTry = manifest.exposed_models?.winds || ['ecmwf', 'gfs', 'hrrr', 'rdps', 'rrfs'];
    for (const m of modelsToTry) {
      if (m === 'blend') continue;
      const res = await getWindFromGrid(lat, lon, m, dateStr, hourStr, manifest);
      if (res) return res;
    }
    return null;
  }

  const modelData = windConfig[model] || {};
  const hours = modelData[dateStr] || [];
  const matchedHour = findNearestHour(hours, hourStr);
  if (!matchedHour) return null;

  const cacheKey = `${model}_${dateStr}_Z${matchedHour}`;
  let grid = windGridCache.get(cacheKey);

  if (!grid) {
    const tileUrl = getTileUrl(manifest, 'winds', model, dateStr, matchedHour);
    const url = appendAuthToken(tileUrl.replace('/{z}/{x}/{y}.webp', '/wind_grid.json'));

    grid = await fetchWindGrid(url);
    if (grid) {
      windGridCache.set(cacheKey, grid);
    } else {
      return null;
    }
  }

  if (!grid || !grid.u || !grid.v) return null;

  const gridW = grid.grid_width;
  const gridH = grid.grid_height;
  const u = grid.u;
  const v = grid.v;

  const west = grid.west;
  const east = grid.east;
  const south = grid.south;
  const north = grid.north;

  if (lon < west || lon > east || lat < south || lat > north) return null;

  const x_pct = (lon - west) / (east - west);
  const y_pct = (north - lat) / (north - south);

  const col = x_pct * (gridW - 1);
  const row = y_pct * (gridH - 1);

  const c0 = Math.floor(col);
  const c1 = Math.min(c0 + 1, gridW - 1);
  const r0 = Math.floor(row);
  const r1 = Math.min(r0 + 1, gridH - 1);

  const idx00 = r0 * gridW + c0;
  const idx01 = r0 * gridW + c1;
  const idx10 = r1 * gridW + c0;
  const idx11 = r1 * gridW + c1;

  const u00 = u[idx00];
  const u01 = u[idx01];
  const u10 = u[idx10];
  const u11 = u[idx11];

  const v00 = v[idx00];
  const v01 = v[idx01];
  const v10 = v[idx10];
  const v11 = v[idx11];

  if (u00 === undefined || u01 === undefined || v00 === undefined || v11 === undefined) {
    return null;
  }

  const tc = col - c0;
  const tr = row - r0;

  const interpU = (1 - tr) * ((1 - tc) * u00 + tc * u01) + tr * ((1 - tc) * u10 + tc * u11);
  const interpV = (1 - tr) * ((1 - tc) * v00 + tc * v01) + tr * ((1 - tc) * v10 + tc * v11);

  const speed = Math.sqrt(interpU * interpU + interpV * interpV);
  const dir = (Math.atan2(-interpU, -interpV) * 180 / Math.PI + 360) % 360;

  return { speed, dir };
}

const aqiTrendCache = new Map<string, any>();

async function getAqiTrendData(model: string, manifest: Manifest): Promise<any | null> {
  const targetModel = ['hrrr', 'raqdps', 'rrfs', 'gefs', 'gefs_dust'].includes(model) ? model : 'hrrr';
  if (aqiTrendCache.has(targetModel)) {
    return aqiTrendCache.get(targetModel);
  }

  const tileRoot = (manifest.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
  const apiServer = manifest.api_server || '';

  let cleanPath = `${tileRoot}/forecast/smoke/aqi_trend_${targetModel}.json`.replace(/\/+/g, '/');
  if (cleanPath.startsWith('/')) {
    cleanPath = cleanPath.substring(1);
  }
  let url = `${apiServer}/${cleanPath}`;
  if (!apiServer && url.startsWith('/')) {
    url = url.substring(1);
  }

  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();
    aqiTrendCache.set(targetModel, data);
    return data;
  } catch (e) {
    return null;
  }
}

function sampleTrendValue(trendData: any, timeStr: string, lat: number, lon: number, seriesKey: string): number | null {
  if (!trendData || !trendData.times || !trendData[seriesKey]) return null;

  let timeIdx = trendData.times.indexOf(timeStr);
  if (timeIdx === -1) {
    const targetMatch = timeStr.match(/^(\d{4}-\d{2}-\d{2})\s+Z(\d{2})$/);
    if (targetMatch) {
      const targetTimeMs = Date.parse(`${targetMatch[1]}T${targetMatch[2]}:00:00Z`);
      let bestIdx = -1;
      let minDiffMs = Infinity;

      trendData.times.forEach((tStr: string, idx: number) => {
        const m = tStr.match(/^(\d{4}-\d{2}-\d{2})\s+Z(\d{2})$/);
        if (m) {
          const tMs = Date.parse(`${m[1]}T${m[2]}:00:00Z`);
          const diffMs = Math.abs(tMs - targetTimeMs);
          if (diffMs < minDiffMs) {
            minDiffMs = diffMs;
            bestIdx = idx;
          }
        }
      });

      if (minDiffMs <= 12 * 3600 * 1000) {
        timeIdx = bestIdx;
      }
    }
  }

  if (timeIdx === -1 || !trendData[seriesKey][timeIdx]) return null;

  const latStep = trendData.lat_step || 0.5;
  const lonStep = trendData.lon_step || 0.5;

  const latIdx = Math.round((lat - trendData.lat_min) / latStep);
  const lonIdx = Math.round((lon - trendData.lon_min) / lonStep);

  const latGridCount = Math.round((trendData.lat_max - trendData.lat_min) / latStep) + 1;
  const lonGridCount = Math.round((trendData.lon_max - trendData.lon_min) / lonStep) + 1;

  if (latIdx >= 0 && latIdx < latGridCount && lonIdx >= 0 && lonIdx < lonGridCount) {
    const val = trendData[seriesKey][timeIdx]?.[latIdx]?.[lonIdx];
    return (val !== undefined && val !== null) ? val : null;
  }
  return null;
}

/**
 * Calculates dew point temperature in Celsius from temperature and relative humidity
 * using the Magnus-Tetens approximation.
 */
export function calculateDewPointC(tempC: number, rh: number): number {
  const a = 17.27;
  const b = 237.7;
  const clampedRh = Math.max(1, Math.min(100, rh));
  const alpha = (a * tempC) / (b + tempC) + Math.log(clampedRh / 100.0);
  return (b * alpha) / (a - alpha);
}

/**
 * Interpolates temperature at a target elevation using multi-level isobaric sounding data.
 * Correctly accounts for nocturnal valley inversions and warm layers aloft.
 */
export function interpolateSoundingTemp(
  targetElevFt: number,
  sfcElevFt: number,
  sfcTempC: number,
  levelTempsC?: Record<number, number>,
  levelHeightsM?: Record<number, number>
): { tempC: number; inversionActive: boolean; inversionCapFt: number; maxInvTempC: number } {
  if (!levelTempsC || !levelHeightsM || Object.keys(levelTempsC).length === 0) {
    const diffC = ((targetElevFt - sfcElevFt) / 3280.84) * 6.5;
    return {
      tempC: sfcTempC - diffC,
      inversionActive: false,
      inversionCapFt: sfcElevFt,
      maxInvTempC: sfcTempC
    };
  }

  // Construct vertical nodes [heightFt, tempC]
  const nodes: { heightFt: number; tempC: number }[] = [
    { heightFt: sfcElevFt, tempC: sfcTempC }
  ];

  const levels = [925, 850, 700, 500, 300];
  for (const lev of levels) {
    const hM = levelHeightsM[lev];
    const tC = levelTempsC[lev];
    if (hM !== undefined && tC !== undefined) {
      const hFt = hM * 3.28084;
      if (hFt > sfcElevFt + 50) { // only include levels above surface
        nodes.push({ heightFt: hFt, tempC: tC });
      }
    }
  }

  // Sort nodes by elevation
  nodes.sort((a, b) => a.heightFt - b.heightFt);

  // Check inversion
  let inversionActive = false;
  let maxInvTempC = sfcTempC;
  let inversionCapFt = sfcElevFt;

  for (let i = 1; i < nodes.length; i++) {
    if (nodes[i].tempC > sfcTempC) {
      inversionActive = true;
      if (nodes[i].tempC > maxInvTempC) {
        maxInvTempC = nodes[i].tempC;
        inversionCapFt = nodes[i].heightFt;
      }
    }
  }

  // If below surface (valley floor)
  if (targetElevFt <= nodes[0].heightFt) {
    if (nodes.length > 1 && inversionActive) {
      // In a nocturnal valley cold pool, temperature falls further or remains cold
      const gamma = (nodes[1].tempC - nodes[0].tempC) / Math.max(100, nodes[1].heightFt - nodes[0].heightFt);
      const clampedGamma = Math.min(0.003, Math.max(0.0, gamma));
      const tempC = nodes[0].tempC - clampedGamma * (nodes[0].heightFt - targetElevFt);
      return { tempC, inversionActive, inversionCapFt, maxInvTempC };
    } else {
      // Standard lapse rate warming down into valley
      const tempC = nodes[0].tempC + ((nodes[0].heightFt - targetElevFt) / 3280.84) * 6.5;
      return { tempC, inversionActive, inversionCapFt, maxInvTempC };
    }
  }

  // If above highest node (above 30,000 ft)
  const last = nodes[nodes.length - 1];
  if (targetElevFt >= last.heightFt) {
    const tempC = last.tempC - ((targetElevFt - last.heightFt) / 3280.84) * 6.5;
    return { tempC, inversionActive, inversionCapFt, maxInvTempC };
  }

  // Piecewise linear interpolation between bracketed levels
  for (let i = 0; i < nodes.length - 1; i++) {
    const n1 = nodes[i];
    const n2 = nodes[i + 1];
    if (targetElevFt >= n1.heightFt && targetElevFt <= n2.heightFt) {
      const frac = (targetElevFt - n1.heightFt) / Math.max(1, n2.heightFt - n1.heightFt);
      const tempC = n1.tempC + frac * (n2.tempC - n1.tempC);
      return { tempC, inversionActive, inversionCapFt, maxInvTempC };
    }
  }

  return { tempC: sfcTempC, inversionActive, inversionCapFt, maxInvTempC };
}

/**
 * Interpolates wind speed at a target elevation using multi-level isobaric sounding data.
 * Accurately accounts for vertical shear, boundary layer friction in valleys, and ridge/summit acceleration.
 */
export function interpolateSoundingWind(
  targetElevFt: number,
  sfcElevFt: number,
  sfcWindMph: number,
  levelWindsMph?: Record<number, number>,
  levelHeightsM?: Record<number, number>
): { windSpeedMph: number } {
  if (!levelWindsMph || !levelHeightsM || Object.keys(levelWindsMph).length === 0) {
    if (targetElevFt >= sfcElevFt) {
      const factor = Math.min(2.5, 1.0 + ((targetElevFt - sfcElevFt) / 1000.0) * 0.10);
      return { windSpeedMph: Math.max(0, Math.round(sfcWindMph * factor)) };
    } else {
      const factor = Math.max(0.4, 1.0 - ((sfcElevFt - targetElevFt) / 1000.0) * 0.12);
      return { windSpeedMph: Math.max(0, Math.round(sfcWindMph * factor)) };
    }
  }

  // Construct vertical nodes [heightFt, windMph]
  const nodes: { heightFt: number; windMph: number }[] = [
    { heightFt: sfcElevFt, windMph: sfcWindMph }
  ];

  const levels = [925, 850, 700, 500, 300];
  for (const lev of levels) {
    const hM = levelHeightsM[lev];
    const wMph = levelWindsMph[lev];
    if (hM !== undefined && wMph !== undefined) {
      const hFt = hM * 3.28084;
      if (hFt > sfcElevFt + 50) {
        nodes.push({ heightFt: hFt, windMph: wMph });
      }
    }
  }

  nodes.sort((a, b) => a.heightFt - b.heightFt);

  // If below lowest node (valley floor)
  if (targetElevFt <= nodes[0].heightFt) {
    const deltaFt = nodes[0].heightFt - targetElevFt;
    const frictionFactor = Math.max(0.35, 1.0 - (deltaFt / 3000.0) * 0.40);
    return { windSpeedMph: Math.max(0, Math.round(nodes[0].windMph * frictionFactor)) };
  }

  // If above highest node
  if (targetElevFt >= nodes[nodes.length - 1].heightFt) {
    return { windSpeedMph: Math.max(0, Math.round(nodes[nodes.length - 1].windMph)) };
  }

  // Piecewise linear interpolation between isobaric nodes
  for (let i = 0; i < nodes.length - 1; i++) {
    const n1 = nodes[i];
    const n2 = nodes[i + 1];
    if (targetElevFt >= n1.heightFt && targetElevFt <= n2.heightFt) {
      const span = Math.max(1, n2.heightFt - n1.heightFt);
      const frac = (targetElevFt - n1.heightFt) / span;
      const w = n1.windMph + frac * (n2.windMph - n1.windMph);
      return { windSpeedMph: Math.max(0, Math.round(w)) };
    }
  }

  return { windSpeedMph: Math.max(0, Math.round(sfcWindMph)) };
}

export interface LocalDateTimeParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  weekdayShort: string;
  ymd: string;
}

export function getLocalDateTimeParts(utcMs: number, tz: string): LocalDateTimeParts {
  const d = new Date(utcMs);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(d);

  let yearStr = '1970';
  let monthStr = '01';
  let dayStr = '01';
  let hourStr = '00';
  let minStr = '00';
  let weekdayShort = 'Sun';

  for (const p of parts) {
    if (p.type === 'year') yearStr = p.value;
    else if (p.type === 'month') monthStr = p.value;
    else if (p.type === 'day') dayStr = p.value;
    else if (p.type === 'hour') hourStr = p.value;
    else if (p.type === 'minute') minStr = p.value;
    else if (p.type === 'weekday') weekdayShort = p.value;
  }

  return {
    year: parseInt(yearStr, 10),
    month: parseInt(monthStr, 10),
    day: parseInt(dayStr, 10),
    hour: parseInt(hourStr, 10) % 24,
    minute: parseInt(minStr, 10),
    weekdayShort,
    ymd: `${yearStr}-${monthStr}-${dayStr}`
  };
}

export function formatLocalYMD(date: Date, tz: string): string {
  return getLocalDateTimeParts(date.getTime(), tz).ymd;
}

/**
 * Converts a local calendar date & time in IANA timezone `tz` to exact UTC epoch milliseconds,
 * handling Daylight Saving Time transitions via a 2-pass offset refinement.
 */
export function localDateTimeToUtcMs(
  year: number,
  month: number,
  day: number,
  hour: number,
  minute: number,
  tz: string
): number {
  const guessUtc = Date.UTC(year, month - 1, day, hour, minute, 0);
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  });

  const toRenderedUtc = (ms: number): number => {
    const parts = fmt.formatToParts(new Date(ms));
    const p: Record<string, number> = {};
    for (const part of parts) {
      if (part.type !== 'literal') {
        p[part.type] = parseInt(part.value, 10);
      }
    }
    return Date.UTC(
      p.year || year,
      (p.month || 1) - 1,
      p.day || 1,
      (p.hour || 0) % 24,
      p.minute || 0,
      p.second || 0
    );
  };

  const refined = guessUtc + (guessUtc - toRenderedUtc(guessUtc));
  return refined + (guessUtc - toRenderedUtc(refined));
}

export class PoiDataService {
  public static getTodayLocalDateStr(lat: number, lon: number, nowDate: Date = new Date()): string {
    try {
      const tz = tzlookup(lat, lon);
      return formatLocalYMD(nowDate, tz);
    } catch {
      return nowDate.toISOString().split('T')[0];
    }
  }

  public static async fetchFullForecast(
    lat: number, 
    lon: number, 
    poiElev: number,
    baseElev: number,
    dateStr: string, 
    manifest: Manifest, 
    isMountain: boolean,
    source: string = 'blend',
    days: number | string = 1,
    requirements?: ForecastLoadRequirements
  ): Promise<DetailedForecastData> {
    const reqs: Required<ForecastLoadRequirements> = {
      needsSolar: requirements?.needsSolar ?? true,
      needsStargazing: requirements?.needsStargazing ?? true,
      needsMountains: requirements?.needsMountains ?? true,
      needsObserved: requirements?.needsObserved ?? true,
    };

    const tz = tzlookup(lat, lon);

    console.log(`Fetching full forecast for ${lat}, ${lon} on ${dateStr} using ${manifest.mode}. Local TZ: ${tz}. Is mountain: ${isMountain}. Source: ${source}. Days: ${days}`);

    const units = store.getState().units;
    const isImperial = units === 'imperial';

    const tx = lng2tile(lon, 4);
    const ty = lat2tile(lat, 4);
    const tileCoords = `4/${tx}/${ty}`;

    let nwsGrid: any = null;
    if (source === 'nws') {
      try {
        nwsGrid = await fetchNwsGridData(lat, lon);
      } catch (e) {
        console.error('Failed to load NWS grid data, falling back to blend:', e);
      }
    }

    let ecccGrid: any = null;
    if (source === 'eccc') {
      try {
        ecccGrid = await fetchEcccGridData(lat, lon);
      } catch (e) {
        console.error('Failed to load ECCC grid data, falling back to blend:', e);
      }
    }

    let pointData: DecodedPointForecast | null = null;
    if (manifest?.forecast_point_tiles?.available) {
      try {
        const pointModel = (source === 'nws' || source === 'eccc') ? 'blend' : source;
        pointData = await ForecastBinaryService.getForecastForPoint(lat, lon, manifest, pointModel);
      } catch (e) {
        console.warn('[PoiDataService] Failed to load binary forecast point tile, falling back to PMTiles:', e);
      }
    }

    const hasPublicAgencyGrid =
      (source === 'nws' && Boolean(nwsGrid?.properties?.temperature?.values)) ||
      (source === 'eccc' && Boolean(ecccGrid?.hourlyForecastGroup?.hourlyForecasts));

    const userTier = TierAccessService.getUserAccessTier();
    const activeAccessDenial: TileAccessDenial | null = pointData
      ? null
      : (ForecastBinaryService.lastAccessDenial ||
          (!TierAccessService.userHasLayerAccess('forecast_point_tiles')
            ? {
                status: userTier === 'free' ? 401 : 402,
                code: userTier === 'free' ? 'AUTH_REQUIRED' : 'SUBSCRIPTION_REQUIRED',
                requiredTier: 'pro',
                userTier,
                message: userTier === 'free'
                  ? 'Sign in or create a free SkySkout account and upgrade to Pro to view forecast trend charts.'
                  : 'SkySkout Pro subscription required to view forecast trend charts.',
                timestamp: Date.now()
              }
            : null));

    const isForecastBlockedByTier = !pointData && !hasPublicAgencyGrid && Boolean(activeAccessDenial);

    // Determine horizon mode and timeline start/end parameters
    const isMax = String(days).toLowerCase() === 'max';
    const MODEL_NOMINAL_MAX_HOURS: Record<string, number> = {
      hrrr: 48,
      rrfs: 84,
      rdps: 84,
      raqdps: 72,
      gefs: 120,
      gefs_dust: 120,
      blend: 240,
      nbm: 264,
      gfs: 240,
      ecmwf: 360,
      gdps: 240,
      icon13: 180
    };

    const modelMaxHours = pointData?.numHours
      ?? (manifest?.forecast_point_tiles as any)?.models_meta?.[source]?.hours
      ?? MODEL_NOMINAL_MAX_HOURS[source]
      ?? 240;

    const now = new Date();
    const currentHourFloorMs = Math.floor(now.getTime() / (3600 * 1000)) * 3600 * 1000;

    let requestedDays = 1;
    const strDays = String(days).trim().toLowerCase();
    const leadingDaysMatch = strDays.match(/^(\d+)/);
    if (leadingDaysMatch) {
      requestedDays = Math.max(1, parseInt(leadingDaysMatch[1], 10));
    }

    // Always anchor all detailed forecast data and trend charts to current time and onward
    const timelineStartMs = currentHourFloorMs;
    let totalHours = 24;

    if (isMax) {
      if (source === 'nws' && nwsGrid?.properties?.temperature?.values) {
        let nwsMaxMs = 0;
        for (const item of nwsGrid.properties.temperature.values) {
          const interval = parseNwsInterval(item.validTime);
          if (interval && interval.end > nwsMaxMs) nwsMaxMs = interval.end;
        }
        const maxNwsHours = nwsMaxMs > currentHourFloorMs ? Math.floor((nwsMaxMs - currentHourFloorMs) / (3600 * 1000)) : 168;
        totalHours = Math.max(24, Math.min(168, maxNwsHours));
      } else if (source === 'eccc' && ecccGrid?.hourlyForecastGroup?.hourlyForecasts) {
        let ecccMaxMs = 0;
        for (const hf of ecccGrid.hourlyForecastGroup.hourlyForecasts) {
          if (hf.timestamp) {
            const ms = Date.parse(hf.timestamp);
            if (!isNaN(ms) && ms > ecccMaxMs) ecccMaxMs = ms;
          }
        }
        const maxEcccHours = ecccMaxMs > currentHourFloorMs ? Math.floor((ecccMaxMs - currentHourFloorMs) / (3600 * 1000)) : 48;
        totalHours = Math.max(12, Math.min(72, maxEcccHours));
      } else if (pointData) {
        const pointDataEndMs = (pointData.cycleEpoch + pointData.numHours * 3600) * 1000;
        const remainingHours = Math.floor((pointDataEndMs - currentHourFloorMs) / (3600 * 1000));
        totalHours = Math.max(1, Math.min(pointData.numHours, remainingHours));
      } else {
        totalHours = modelMaxHours;
      }
    } else if (requestedDays > 1) {
      // Multi-day options (3D, 5D, 7D, etc.): clamp to available model extent from current hour onward
      if (pointData) {
        const pointDataEndMs = (pointData.cycleEpoch + pointData.numHours * 3600) * 1000;
        const availableFromNow = Math.floor((pointDataEndMs - currentHourFloorMs) / (3600 * 1000));
        const extentCap = Math.max(
          1,
          Math.min(requestedDays * 24, availableFromNow > 0 ? availableFromNow : pointData.numHours, pointData.numHours)
        );
        totalHours = Math.min(requestedDays * 24, extentCap);
      } else {
        totalHours = Math.min(requestedDays * 24, modelMaxHours);
      }
    } else {
      if (pointData) {
        const pointDataEndMs = (pointData.cycleEpoch + pointData.numHours * 3600) * 1000;
        const availableFromNow = Math.floor((pointDataEndMs - currentHourFloorMs) / (3600 * 1000));
        totalHours = availableFromNow > 0 ? Math.max(1, Math.min(24, availableFromNow)) : 24;
      } else {
        totalHours = 24;
      }
    }

    const localHours = isForecastBlockedByTier ? [] : Array.from({ length: totalHours }, (_, i) => i);

    const timeLabels = localHours.map(h => {
      const targetUtcMs = timelineStartMs + h * 3600 * 1000;
      const lp = getLocalDateTimeParts(targetUtcMs, tz);
      const localHourStr = String(lp.hour).padStart(2, '0');
      return `${lp.weekdayShort} ${localHourStr}:00`;
    });

    const hourlyDataPromises = localHours.map(async (h) => {
      const targetUtcMs = timelineStartMs + h * 3600 * 1000;
      const utcDate = new Date(targetUtcMs);
      const utcDateStr = utcDate.toISOString().split('T')[0];
      const utcHourStr = String(utcDate.getUTCHours()).padStart(2, '0');

      let temp: number | null = null;
      let cloud: number | null = null;
      let precip: number | null = null;
      let precipRate: number | null = null;
      let snowRate: number | null = null;
      let speed: number | null = null;
      let dir: number | null = null;
      let thunderstorm: number | null = null;
      let darkskies: number | null = null;
      let dewRisk: number | null = null;
      let seeing: number | null = null;
      let stepIdx: number | null = null;

      // 0. Fetch from fast spatial binary point tile if loaded
      if (pointData && pointData.cycleEpoch) {
        const targetUtcSec = Math.floor(targetUtcMs / 1000);
        const hourDiff = Math.floor((targetUtcSec - pointData.cycleEpoch) / 3600);
        const sIdx = ForecastBinaryService.getStepIndexForHour(hourDiff, pointData.numHours);
        if (hourDiff >= 0 && sIdx < pointData.numHours) {
          stepIdx = sIdx;
          temp = pointData.tempsC[stepIdx];
          cloud = pointData.cloudCoverTotal[stepIdx];
          precip = pointData.precipProb[stepIdx];
          precipRate = pointData.precipRateMmH[stepIdx] / 25.4; // mm to inches
          if (pointData.snowRateCmH && pointData.snowRateCmH[stepIdx] !== undefined) {
            snowRate = pointData.snowRateCmH[stepIdx] / 2.54; // cm to inches
          }
          speed = pointData.windSpeedMph[stepIdx] / 2.23694; // mph to m/s
          dir = pointData.windDirDeg[stepIdx];
          thunderstorm = (pointData.lightningHazard && pointData.lightningHazard[stepIdx] !== undefined)
            ? pointData.lightningHazard[stepIdx]
            : null;
          if (pointData.darkskiesScore && pointData.darkskiesScore[stepIdx] !== undefined) {
            darkskies = pointData.darkskiesScore[stepIdx];
          }
          if (pointData.dewRiskScore && pointData.dewRiskScore[stepIdx] !== undefined) {
            dewRisk = pointData.dewRiskScore[stepIdx];
          }
          if (pointData.seeingScore && pointData.seeingScore[stepIdx] !== undefined) {
            seeing = pointData.seeingScore[stepIdx];
          }
        }
      }

      // 1. Fetch from ECCC/NWS grid if selected and available
      if (source === 'eccc' && ecccGrid) {
        const hourlyForecasts = ecccGrid.hourlyForecastGroup?.hourlyForecasts;
        const hourlyForecast = lookupEcccHourlyForecast(hourlyForecasts, targetUtcMs);
        if (hourlyForecast) {
          const ecccTemp = hourlyForecast.temperature?.value?.en ?? hourlyForecast.temperature?.value?.fr ?? null;
          if (ecccTemp !== null) temp = ecccTemp;
          const ecccLop = hourlyForecast.lop?.value?.en ?? hourlyForecast.lop?.value?.fr ?? null;
          if (ecccLop !== null) precip = ecccLop;
          const ws_km_h = hourlyForecast.wind?.speed?.value?.en ?? hourlyForecast.wind?.speed?.value?.fr ?? null;
          if (ws_km_h !== null) {
            speed = ws_km_h / 3.6; // Convert km/h to m/s
          }
          const dirStr = hourlyForecast.wind?.direction?.value?.en ?? hourlyForecast.wind?.direction?.value?.fr ?? null;
          if (dirStr) {
            dir = compassToDegrees(dirStr);
          }
          const condStr = hourlyForecast.condition?.en ?? hourlyForecast.condition?.fr ?? null;
          if (condStr) {
            cloud = estimateCloudCoverFromCondition(condStr);
          }
        }
      } else if (source === 'nws' && nwsGrid) {
        const nwsTemp = lookupNwsGridValue(nwsGrid.properties.temperature, targetUtcMs);
        if (nwsTemp !== null) temp = nwsTemp;
        const nwsCloud = lookupNwsGridValue(nwsGrid.properties.skyCover, targetUtcMs);
        if (nwsCloud !== null) cloud = nwsCloud;
        const nwsPop = lookupNwsGridValue(nwsGrid.properties.probabilityOfPrecipitation, targetUtcMs);
        if (nwsPop !== null) precip = nwsPop;
        const nwsQpf = lookupNwsGridValue(nwsGrid.properties.quantitativePrecipitation, targetUtcMs);
        if (nwsQpf !== null) precipRate = nwsQpf / 25.4;
        const nwsSnow = lookupNwsGridValue((nwsGrid.properties as any).snowfallAmount, targetUtcMs);
        if (nwsSnow !== null) snowRate = nwsSnow / 25.4;
        const ws_km_h = lookupNwsGridValue(nwsGrid.properties.windSpeed, targetUtcMs);
        if (ws_km_h !== null) {
          speed = ws_km_h / 3.6; // Convert km/h to m/s
        }
        const nwsDir = lookupNwsGridValue(nwsGrid.properties.windDirection, targetUtcMs);
        if (nwsDir !== null) dir = nwsDir;
        const nwsTstm = lookupNwsGridValue((nwsGrid.properties as any).probabilityOfThunderstorms, targetUtcMs) ?? lookupNwsGridValue((nwsGrid.properties as any).probabilityOfThunder, targetUtcMs);
        if (nwsTstm !== null) thunderstorm = nwsTstm;
      } else if (temp === null) {
        // 2. Fetch from model-specific tile/JSON decoders
        temp = await getTempForModelAt(lat, lon, source, utcDateStr, utcHourStr, manifest);
        cloud = await getCloudCoverAt(lat, lon, source, utcDateStr, utcHourStr, manifest);
        precip = await getPrecipLikelihoodAt(lat, lon, source, utcDateStr, utcHourStr, manifest);

        const wind = await getWindFromGrid(lat, lon, source, utcDateStr, utcHourStr, manifest);
        if (wind) {
          speed = wind.speed;
          dir = wind.dir;
        }
      }

      // 3. Fallbacks to blend / defaults if any value is null
      if (temp === null) {
        if (source !== 'blend') {
          temp = await getTempForModelAt(lat, lon, 'blend', utcDateStr, utcHourStr, manifest);
        }
        if (temp === null) {
          temp = 15;
        }
      }
      if (cloud === null) {
        if (source !== 'blend') {
          cloud = await getCloudCoverAt(lat, lon, 'blend', utcDateStr, utcHourStr, manifest);
        }
        if (cloud === null) {
          cloud = 0;
        }
      }
      if (precip === null) {
        if (source !== 'blend') {
          precip = await getPrecipLikelihoodAt(lat, lon, 'blend', utcDateStr, utcHourStr, manifest);
        }
        if (precip === null) {
          precip = 0;
        }
      }
      if (speed === null || dir === null) {
        if (source !== 'blend') {
          const wind = await getWindFromGrid(lat, lon, 'blend', utcDateStr, utcHourStr, manifest);
          if (wind) {
            speed = speed ?? wind.speed;
            dir = dir ?? wind.dir;
          }
        }
        speed = speed ?? 0;
        dir = dir ?? 0;
      }
      if (thunderstorm === null) {
        if (pointData && pointData.lightningHazard && stepIdx !== null && pointData.lightningHazard[stepIdx] !== undefined) {
          thunderstorm = pointData.lightningHazard[stepIdx];
        } else {
          thunderstorm = 0;
        }
      }

      return { temp, cloud, precip, precipRate, snowRate, speed, dir, thunderstorm, darkskies, dewRisk, seeing, utcDateStr, utcHourStr, stepIdx };
    });

    const hourlyResults = await Promise.all(hourlyDataPromises);

    const rawTemps = hourlyResults.map(r => r.temp);
    let lastValidCloud = 0;
    const cloudCover = hourlyResults.map(r => {
      if (r.cloud !== null && r.cloud >= 0) {
        lastValidCloud = r.cloud;
        return r.cloud;
      }
      return lastValidCloud;
    });
    const precipLikelihood = hourlyResults.map(r => r.precip ?? 0);
    const precipRate = hourlyResults.map(r => r.precipRate ?? 0);
    const snowRate = hourlyResults.map(r => r.snowRate ?? 0);
    const thunderstormProb = hourlyResults.map(r => r.thunderstorm ?? 0);
    const windSpeed = hourlyResults.map(r => r.speed);
    const windDir = hourlyResults.map(r => r.dir);
    const darkskiesScores = hourlyResults.map(r => {
      if (r.darkskies !== null && !isNaN(r.darkskies)) {
        return r.darkskies > 10 ? Number((r.darkskies / 10).toFixed(1)) : Number(r.darkskies.toFixed(1));
      }
      return null;
    });
    const dewRiskHourly = hourlyResults.map(r => r.dewRisk ?? 0);
    const seeingScores = hourlyResults.map(r => (r.seeing !== null && r.seeing !== undefined) ? r.seeing : null);
    const seeingArcsec = seeingScores.map(s => s !== null ? seeingScoreToArcsec(s) : null);

    let minElev = poiElev;
    let maxElev = poiElev;
    if (reqs.needsMountains) {
      try {
        const profile = await getElevationAt(lat, lon);
        minElev = profile.minElevation;
        maxElev = profile.maxElevation;
      } catch (e) {
        console.warn('Failed to get elevation profile:', e);
      }
    }

    let forecastElev = baseElev;
    if (source === 'nws' && nwsGrid?.properties?.elevation) {
      const valM = nwsGrid.properties.elevation.value;
      if (valM !== undefined && valM !== null) {
        forecastElev = valM * 3.28084;
      }
    } else if (source === 'eccc' && ecccGrid) {
      const valM = ecccGrid.elevation ?? ecccGrid.elev ?? ecccGrid.station_elevation ?? null;
      if (valM !== null) {
        forecastElev = parseFloat(valM) * 3.28084;
      }
    }

    // Freezing level calculation
    const freezingElevs = hourlyResults.map((r, idx) => {
      const sIdx = (r.stepIdx !== null && r.stepIdx !== undefined) ? r.stepIdx : idx;
      if (pointData && sIdx >= 0 && sIdx < pointData.freezingLevelFt.length && pointData.freezingLevelFt[sIdx] > 0) {
        return pointData.freezingLevelFt[sIdx];
      }
      const elevM = (poiElev / 3.28084) + (r.temp / 0.0065);
      return Math.max(0, elevM * 3.28084);
    });
    const avgFreezingElev = freezingElevs.length > 0
      ? Math.round(freezingElevs.reduce((a, b) => a + b, 0) / freezingElevs.length)
      : Math.round(poiElev || baseElev || 0);

    const tempsForecast = rawTemps.map(t => {
      return isImperial ? Math.round(t * 1.8 + 32) : Math.round(t);
    });

    const soundingColumns: AtmosphericColumn[] = [];
    const inversionMetricsList: InversionMetrics[] = [];
    const soundingLevels = [925, 850, 700, 500, 300];

    const tempsHighest: number[] = [];
    const tempsLowest: number[] = [];
    const tempsMid: number[] = [];
    const tempsPoi: number[] = [];
    const midElev = (maxElev + minElev) / 2;

    if (reqs.needsMountains) {
      for (let idx = 0; idx < hourlyResults.length; idx++) {
        const r = hourlyResults[idx];
        const sIdx = r.stepIdx;
        const sfcTempC = rawTemps[idx];
        const sfcTempF = Math.round((sfcTempC * 1.8 + 32) * 10) / 10;
        const sfcElevM = Math.round(forecastElev / 3.28084);
        const sfcRh = (pointData && sIdx !== null && sIdx < pointData.relativeHumidity.length)
          ? pointData.relativeHumidity[sIdx]
          : 60;
        const sfcDewPointC = calculateDewPointC(sfcTempC, sfcRh);
        const sfcDewPointF = Math.round((sfcDewPointC * 1.8 + 32) * 10) / 10;
        const sfcWindMph = Math.round((r.speed ?? 0) * 2.23694);
        const sfcWindDir = Math.round(r.dir ?? 0);

        let levelTempsC: Record<number, number> | undefined;
        let levelHeightsM: Record<number, number> | undefined;
        const levelsData: SoundingLevelData[] = [];

        if (pointData && pointData.hasSounding && sIdx !== null && sIdx < pointData.numHours) {
          levelTempsC = {};
          levelHeightsM = {};
          for (const lev of soundingLevels) {
            const tC = pointData.soundingTempsC?.[lev]?.[sIdx] ?? (sfcTempC - ((1000 - lev) * 0.007));
            const hM = pointData.soundingHeightsM?.[lev]?.[sIdx] ?? Math.round(44330 * (1 - Math.pow(lev / 1013.25, 0.190284)));
            const rh = pointData.soundingRh?.[lev]?.[sIdx] ?? 50;
            const wspd = pointData.soundingWindSpeedMph?.[lev]?.[sIdx] ?? sfcWindMph;
            const wdir = pointData.soundingWindDirDeg?.[lev]?.[sIdx] ?? sfcWindDir;
            const dpC = calculateDewPointC(tC, rh);

            levelTempsC[lev] = tC;
            levelHeightsM[lev] = hM;

            levelsData.push({
              levelHpa: lev,
              tempC: Math.round(tC * 10) / 10,
              tempF: Math.round((tC * 1.8 + 32) * 10) / 10,
              heightM: Math.round(hM),
              heightFt: Math.round(hM * 3.28084),
              dewPointC: Math.round(dpC * 10) / 10,
              dewPointF: Math.round((dpC * 1.8 + 32) * 10) / 10,
              relativeHumidity: rh,
              windSpeedMph: wspd,
              windDirDeg: wdir
            });
          }
        } else {
          // Standard atmospheric fallback for levels
          levelTempsC = {};
          levelHeightsM = {};
          for (const lev of soundingLevels) {
            const stdH = Math.round(44330 * (1 - Math.pow(lev / 1013.25, 0.190284)));
            const tC = sfcTempC - ((stdH - sfcElevM) * 0.0065);
            const rh = Math.max(20, Math.min(90, sfcRh - ((stdH - sfcElevM) / 1000) * 10));
            const dpC = calculateDewPointC(tC, rh);

            levelTempsC[lev] = tC;
            levelHeightsM[lev] = stdH;

            levelsData.push({
              levelHpa: lev,
              tempC: Math.round(tC * 10) / 10,
              tempF: Math.round((tC * 1.8 + 32) * 10) / 10,
              heightM: stdH,
              heightFt: Math.round(stdH * 3.28084),
              dewPointC: Math.round(dpC * 10) / 10,
              dewPointF: Math.round((dpC * 1.8 + 32) * 10) / 10,
              relativeHumidity: Math.round(rh),
              windSpeedMph: sfcWindMph,
              windDirDeg: sfcWindDir
            });
          }
        }

        const interpHigh = interpolateSoundingTemp(maxElev, forecastElev, sfcTempC, levelTempsC, levelHeightsM);
        const interpLow = interpolateSoundingTemp(minElev, forecastElev, sfcTempC, levelTempsC, levelHeightsM);
        const interpMid = interpolateSoundingTemp(midElev, forecastElev, sfcTempC, levelTempsC, levelHeightsM);
        const interpPoi = interpolateSoundingTemp(poiElev, forecastElev, sfcTempC, levelTempsC, levelHeightsM);

        tempsHighest.push(isImperial ? Math.round(interpHigh.tempC * 1.8 + 32) : Math.round(interpHigh.tempC));
        tempsLowest.push(isImperial ? Math.round(interpLow.tempC * 1.8 + 32) : Math.round(interpLow.tempC));
        tempsMid.push(isImperial ? Math.round(interpMid.tempC * 1.8 + 32) : Math.round(interpMid.tempC));
        tempsPoi.push(isImperial ? Math.round(interpPoi.tempC * 1.8 + 32) : Math.round(interpPoi.tempC));

        let maxAloftC = sfcTempC;
        let capElevFt = forecastElev;
        for (const l of levelsData) {
          if (l.heightFt > forecastElev && l.tempC > maxAloftC) {
            maxAloftC = l.tempC;
            capElevFt = l.heightFt;
          }
        }
        const invActive = maxAloftC > sfcTempC;
        const invStrengthF = invActive ? Math.round(((maxAloftC - sfcTempC) * 1.8) * 10) / 10 : 0;
        const invCapFt = invActive ? capElevFt : forecastElev;
        const lapseF = (capElevFt > forecastElev)
          ? Math.round((((maxAloftC - sfcTempC) * 1.8) / ((capElevFt - forecastElev) / 1000)) * 10) / 10
          : -3.6;

        inversionMetricsList.push({
          active: invActive,
          inversionDiffF: invStrengthF,
          inversionCapFt: invCapFt,
          surfaceTempF: sfcTempF,
          capTempF: Math.round((maxAloftC * 1.8 + 32) * 10) / 10,
          lapseRateFPer1k: lapseF
        });

        soundingColumns.push({
          hourIdx: idx,
          timeLabel: timeLabels[idx] || `+${idx}h`,
          surface: {
            tempC: Math.round(sfcTempC * 10) / 10,
            tempF: sfcTempF,
            elevationM: sfcElevM,
            elevationFt: forecastElev,
            dewPointC: Math.round(sfcDewPointC * 10) / 10,
            dewPointF: sfcDewPointF,
            relativeHumidity: sfcRh,
            windSpeedMph: sfcWindMph,
            windDirDeg: sfcWindDir
          },
          levels: levelsData,
          inversionActive: invActive,
          inversionStrengthF: invStrengthF,
          inversionCapFt: invCapFt,
          freezingLevelFt: freezingElevs[idx] || 0
        });
      }
    } else {
      for (let idx = 0; idx < hourlyResults.length; idx++) {
        const tF = tempsForecast[idx];
        tempsHighest.push(tF);
        tempsLowest.push(tF);
        tempsMid.push(tF);
        tempsPoi.push(tF);
      }
    }

    // Keep legacy variables for backwards compatibility
    const tempsFlat = tempsForecast;
    const tempsMountain = tempsHighest;

    // Real Smoke and AQI lookups from binary tile or trend dataset
    let smokeDensity: number[];
    let aqiValues: number[];
    let aqiPointData: DecodedPointForecast | null = pointData;
    const hasNativeAerosol =
      ['hrrr', 'rrfs', 'raqdps', 'gefs', 'gefs_dust'].includes(source) ||
      Boolean(pointData && (pointData.aqiValues.some(v => v > 0) || pointData.smokeSfc.some(v => v > 0)));
    if (
      !hasNativeAerosol &&
      manifest?.forecast_point_tiles?.available &&
      manifest.forecast_point_tiles.available_models?.includes('gefs')
    ) {
      try {
        const gefsPoint = await ForecastBinaryService.getForecastForPoint(lat, lon, manifest, 'gefs');
        if (gefsPoint && gefsPoint.numHours > 0) {
          aqiPointData = gefsPoint;
        }
      } catch (_e) {
        // Retain primary pointData if GEFS aerosol lookup fails
      }
    }
    if (aqiPointData && aqiPointData.cycleEpoch && (aqiPointData.isAerosolOnly || hasNativeAerosol)) {
      smokeDensity = hourlyResults.map((r, idx) => {
        const targetUtcSec = Math.floor(Date.parse(`${r.utcDateStr}T${r.utcHourStr}:00:00Z`) / 1000);
        const hDiff = Math.floor((targetUtcSec - aqiPointData!.cycleEpoch) / 3600);
        const sIdx = hDiff >= 0
          ? ForecastBinaryService.getStepIndexForHour(hDiff, aqiPointData!.numHours)
          : (r.stepIdx ?? idx);
        return Math.round(aqiPointData!.smokeSfc[sIdx] ?? 0);
      });
      aqiValues = hourlyResults.map((r, idx) => {
        const targetUtcSec = Math.floor(Date.parse(`${r.utcDateStr}T${r.utcHourStr}:00:00Z`) / 1000);
        const hDiff = Math.floor((targetUtcSec - aqiPointData!.cycleEpoch) / 3600);
        const sIdx = hDiff >= 0
          ? ForecastBinaryService.getStepIndexForHour(hDiff, aqiPointData!.numHours)
          : (r.stepIdx ?? idx);
        return Math.round(aqiPointData!.aqiValues[sIdx] ?? 0);
      });
    } else if (pointData) {
      smokeDensity = hourlyResults.map((r, idx) => Math.round(pointData!.smokeSfc[r.stepIdx ?? idx] || 0));
      aqiValues = hourlyResults.map((r, idx) => Math.round(pointData!.aqiValues[r.stepIdx ?? idx] || 50));
    } else if (!TierAccessService.userHasLayerAccess('aqi_forecast') || hourlyResults.length === 0) {
      smokeDensity = [];
      aqiValues = [];
    } else {
      const trendData = await getAqiTrendData(source, manifest);

      let lastValidSmoke = 0;
      smokeDensity = hourlyResults.map(r => {
        const timeStr = `${r.utcDateStr} Z${r.utcHourStr}`;
        let val = sampleTrendValue(trendData, timeStr, lat, lon, 'smoke_sfc');
        if (val === null || val === 0) {
          const eatm = sampleTrendValue(trendData, timeStr, lat, lon, 'smoke_eatm');
          if (eatm && eatm > 0) {
            val = Math.max(val || 0, Math.round(eatm * 12));
          }
        }
        if (val !== null && val > 0) {
          lastValidSmoke = val;
          return val;
        } else if (lastValidSmoke > 0) {
          return lastValidSmoke;
        }
        return 0;
      });

      let lastValidAqi = 50;
      let hasSeenValidAqi = false;
      aqiValues = hourlyResults.map(r => {
        const timeStr = `${r.utcDateStr} Z${r.utcHourStr}`;
        const val = sampleTrendValue(trendData, timeStr, lat, lon, 'aqi');
        if (val !== null && val > 0) {
          lastValidAqi = val;
          hasSeenValidAqi = true;
          return val;
        } else if (hasSeenValidAqi) {
          return lastValidAqi;
        }
        return val !== null ? val : 50;
      });
    }

    // Load Kp forecast & space weather telemetry once (only if stargazing required)
    let kpData: any[] = [];
    let spaceWeatherTelemetry: DetailedForecastData['spaceWeather'] | undefined = undefined;
    if (reqs.needsStargazing) {
      try {
        const apiServer = getApiServer(manifest).replace(/\/$/, '');
        const tileRoot = (manifest.tile_base || 'tiles').replace(/^\/+|\/+$/g, '');
        const telemetryUrls = [
          apiServer ? `${apiServer}/${tileRoot}/observed/aurora/space_weather_telemetry.json` : '',
          'space_weather_telemetry.json'
        ].filter(Boolean);

        let loadedTelemetry: any = null;
        for (const tUrl of telemetryUrls) {
          try {
            const tRes = await fetch(tUrl);
            if (tRes.ok) {
              loadedTelemetry = await tRes.json();
              break;
            }
          } catch (_) {}
        }

        if (loadedTelemetry && typeof loadedTelemetry === 'object') {
          if (Array.isArray(loadedTelemetry.kp_forecast)) {
            kpData = loadedTelemetry.kp_forecast;
          }
          const cur = loadedTelemetry.current || {};
          spaceWeatherTelemetry = {
            currentKp: typeof cur.kp === 'number' ? cur.kp : 2.5,
            tonightMaxKp: typeof loadedTelemetry.tonight?.max_kp === 'number' ? loadedTelemetry.tonight.max_kp : (typeof cur.kp === 'number' ? cur.kp : 2.5),
            tomorrowMaxKp: typeof loadedTelemetry.tomorrow?.max_kp === 'number' ? loadedTelemetry.tomorrow.max_kp : (typeof cur.kp === 'number' ? cur.kp : 2.5),
            solarWindSpeedKms: typeof cur.solar_wind_speed_kms === 'number' ? cur.solar_wind_speed_kms : null,
            solarWindDensityCm3: typeof cur.solar_wind_density_cm3 === 'number' ? cur.solar_wind_density_cm3 : null,
            bzNt: typeof cur.bz_nt === 'number' ? cur.bz_nt : null,
            btNt: typeof cur.bt_nt === 'number' ? cur.bt_nt : null,
            hpiNorthGw: typeof cur.hpi_north_gw === 'number' ? cur.hpi_north_gw : null,
            hpiSouthGw: typeof cur.hpi_south_gw === 'number' ? cur.hpi_south_gw : null,
            propagatedTimeTag: cur.propagated_time_tag || null,
            updatedAt: loadedTelemetry.updated_at || null
          };
        }

        if (kpData.length === 0) {
          const kpUrls = [
            apiServer ? `${apiServer}/${tileRoot}/observed/aurora/noaa_kp_forecast.json` : '',
            'noaa_kp_forecast.json'
          ].filter(Boolean);
          for (const kUrl of kpUrls) {
            try {
              const res = await fetch(kUrl);
              if (res.ok) {
                kpData = await res.json();
                break;
              }
            } catch (_) {}
          }
        }
      } catch (e) {
        console.error("Failed to load space weather forecast in PoiDataService:", e);
      }
    }

    const darkskiesModel = store.getState().selectedModels['darkskies'] || 'blend';
    const targetDate = now;
    const nowLocalParts = getLocalDateTimeParts(now.getTime(), tz);
    const getLocalNoonDate = (dayOffset: number): Date =>
      new Date(localDateTimeToUtcMs(nowLocalParts.year, nowLocalParts.month, nowLocalParts.day + dayOffset, 12, 0, tz));
    const formatTime = (d: Date | null | undefined) => d ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: tz }) : '--:--';

    const horizonDayCount = isMax ? Math.max(1, Math.ceil(totalHours / 24)) : requestedDays;

    const darkskiesTrends = [];
    if (reqs.needsStargazing) {
      for (let i = 0; i < horizonDayCount; i++) {
      const d_i = getLocalNoonDate(i);
      const nextDayNoon = getLocalNoonDate(i + 1);
      const curDateStr = formatLocalYMD(d_i, tz);

      const sTimes_i = SunCalc.getTimes(d_i, lat, lon);
      const mTimes_i = SunCalc.getMoonTimes(d_i, lat, lon);
      const mDetails_i = getMoonPhaseDetails(d_i);

      let curMoonSet_i = mTimes_i.set;
      if (!curMoonSet_i && mTimes_i.rise) {
        const nextDayMoon = SunCalc.getMoonTimes(nextDayNoon, lat, lon);
        if (nextDayMoon.set && (!nextDayMoon.rise || nextDayMoon.set < nextDayMoon.rise)) {
          curMoonSet_i = nextDayMoon.set;
        }
      }

      const astroDawnTime_i = formatTime(sTimes_i.nightEnd);
      const astroDuskTime_i = formatTime(sTimes_i.night);
      const moonRiseTime_i = formatTime(mTimes_i.rise);
      const moonSetTime_i = formatTime(curMoonSet_i);

      const moonIcon_i = mDetails_i.icon;
      const moonName_i = mDetails_i.name;
      const moonIllumination_i = mDetails_i.illumination;

      // Darkskies forecast trend (Hour-by-hour from Sunset to Sunrise, starting >= current hour for today)
      const sunsetDt = sTimes_i.sunset;
      const nextSTimes = SunCalc.getTimes(nextDayNoon, lat, lon);
      const sunriseDt = nextSTimes.sunrise || sTimes_i.nightEnd || new Date(d_i.getTime() + 12 * 3600 * 1000);

      const nightSteps: { date: string; hour: string; label: string }[] = [];
      if (sunsetDt && sunriseDt && !isNaN(sunsetDt.getTime()) && !isNaN(sunriseDt.getTime())) {
        let curStepMs = new Date(sunsetDt).setMinutes(0, 0, 0);
        if (i === 0 && curStepMs < currentHourFloorMs) {
          curStepMs = currentHourFloorMs;
        }
        const endStepMs = new Date(sunriseDt).setMinutes(0, 0, 0) + 3600 * 1000;
        
        while (curStepMs <= endStepMs) {
          const stepDt = new Date(curStepMs);
          const stepDateStr = stepDt.toISOString().split('T')[0];
          const hourStr = String(stepDt.getUTCHours()).padStart(2, '0');
          const label = stepDt.toLocaleTimeString([], { hour: 'numeric', timeZone: tz });
          nightSteps.push({ date: stepDateStr, hour: hourStr, label });
          curStepMs += 3600 * 1000;
        }
      }

      if (nightSteps.length === 0) {
        const darkskiesHours = manifest.darkskies?.available_by_model?.[darkskiesModel]?.[curDateStr] || ['00', '03', '06', '09', '12', '15', '18', '21'];
        darkskiesHours.forEach(h => {
          const parts = curDateStr.split('-').map(Number);
          const dt = new Date(Date.UTC(parts[0], parts[1] - 1, parts[2], parseInt(h)));
          if (i > 0 || dt.getTime() >= currentHourFloorMs) {
            nightSteps.push({
              date: curDateStr,
              hour: h,
              label: dt.toLocaleTimeString([], { hour: 'numeric', timeZone: tz })
            });
          }
        });
      }

      let scores: number[] = [];
      if (pointData) {
        scores = nightSteps.map(step => {
          const stepUtcMs = new Date(`${step.date}T${step.hour.replace('Z', '')}:00:00Z`).getTime();
          const hourDiff = Math.floor((Math.floor(stepUtcMs / 1000) - pointData!.cycleEpoch) / 3600);
          const stepIdx = ForecastBinaryService.getStepIndexForHour(hourDiff, pointData!.numHours);
          if (hourDiff >= 0 && stepIdx < pointData!.numHours) {
            return pointData!.darkskiesScore[stepIdx];
          }
          return 0;
        });
      } else if (TierAccessService.userHasLayerAccess('darkskies')) {
        const scorePromises = nightSteps.map(step => {
          const url = getTileUrl(manifest, 'darkskies', darkskiesModel, step.date, step.hour);
          const probeZoom = 4;
          const tx = lng2tile(lon, probeZoom);
          const ty = lat2tile(lat, probeZoom);
          const tileUrl = buildSampleTileUrl(url, `${probeZoom}/${tx}/${ty}`);
          return getDarkskiesScoreAt(lat, lon, tileUrl);
        });
        scores = await Promise.all(scorePromises);
      } else {
        scores = nightSteps.map(() => 0);
      }
      const labels = nightSteps.map(s => s.label);
      const darkskiesScore_i = scores.length > 0 ? Math.max(...scores) : 0;

      // Kp forecast trend for this local calendar day (non-overlapping across days, starting at current 3h bin for today)
      const localDayStartMs = localDateTimeToUtcMs(nowLocalParts.year, nowLocalParts.month, nowLocalParts.day + i, 0, 0, tz);
      const localNextDayStartMs = localDateTimeToUtcMs(nowLocalParts.year, nowLocalParts.month, nowLocalParts.day + i + 1, 0, 0, tz);
      const kpStartMs = i === 0 ? Math.max(localDayStartMs, currentHourFloorMs - 2 * 3600 * 1000) : localDayStartMs;
      const kpEndMs = (horizonDayCount === 1 && i === 0) ? (currentHourFloorMs + 24 * 3600 * 1000) : localNextDayStartMs;
      const dayKpRecords = kpData.filter((r: any) => {
        const t = new Date(r.time_tag + 'Z').getTime();
        return t >= kpStartMs && t < kpEndMs;
      });
      const kpValues = dayKpRecords.map((r: any) => r.kp);
      const kpLabels = dayKpRecords.map((r: any) => {
        const dt = new Date(r.time_tag + 'Z');
        const wk = dt.toLocaleDateString('en-US', { weekday: 'short', timeZone: tz });
        const tm = dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', timeZone: tz });
        return `${wk} ${tm}`;
      });

      // Aurora probability
      const auroraDateStr = getAuroraTargetDate(d_i);
      const isAuroraAvailable = store.getState().auroraAvailableDates.includes(auroraDateStr);
      let auroraProb = 0;
      if (isAuroraAvailable && TierAccessService.userHasLayerAccess('aurora')) {
        try {
          const url = getTileUrl(manifest, 'aurora', '', auroraDateStr, '');
          const probeZoom = 4;
          const tx = lng2tile(lon, probeZoom);
          const ty = lat2tile(lat, probeZoom);
          const tileUrl = buildSampleTileUrl(url, `${probeZoom}/${tx}/${ty}`);
          const pixel = await getPixelAt(lat, lon, tileUrl);
          auroraProb = (pixel && pixel[3] > 0) ? pixel[2] : 0;
        } catch (e) {
          console.error("Error probing aurora tile:", e);
        }
      }

      darkskiesTrends.push({
        date: curDateStr,
        dateLabel: d_i.toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric', timeZone: tz }),
        labels,
        scores,
        kpLabels,
        kpValues,
        auroraProb,
        isAuroraAvailable,
        auroraDateStr,
        moonIcon: moonIcon_i,
        moonName: moonName_i,
        moonIllumination: moonIllumination_i,
        moonRiseTime: moonRiseTime_i,
        moonSetTime: moonSetTime_i,
        astroDawnTime: astroDawnTime_i,
        astroDuskTime: astroDuskTime_i,
        darkskiesScore: darkskiesScore_i
      });
      }
    }

    const todayNoon = getLocalNoonDate(0);
    const sTimes_0 = SunCalc.getTimes(todayNoon, lat, lon);
    const mTimes_0 = SunCalc.getMoonTimes(todayNoon, lat, lon);
    const mDetails_0 = getMoonPhaseDetails(todayNoon);

    const astroDawnTime = darkskiesTrends[0]?.astroDawnTime ?? formatTime(sTimes_0.nightEnd || sTimes_0.dawn);
    const astroDuskTime = darkskiesTrends[0]?.astroDuskTime ?? formatTime(sTimes_0.night || sTimes_0.dusk);
    const darkskiesScore = darkskiesTrends[0]?.darkskiesScore ?? 0;
    const moonIcon = darkskiesTrends[0]?.moonIcon ?? mDetails_0.icon;
    const moonName = darkskiesTrends[0]?.moonName ?? mDetails_0.name;
    const moonIllumination = darkskiesTrends[0]?.moonIllumination ?? mDetails_0.illumination;
    const moonRiseTime = darkskiesTrends[0]?.moonRiseTime ?? formatTime(mTimes_0.rise);
    const moonSetTime = darkskiesTrends[0]?.moonSetTime ?? formatTime(mTimes_0.set);

    const moonPhaseOutlook = [];
    const solarOutlook = [];
    
    // Build array of promises for tile probes
    const scorePromises = [];
    
    // Dynamically sized moonPhaseOutlook (at least 7 days, starting with today in POI timezone)
    const moonDayCount = Math.max(7, horizonDayCount);
    for (let i = 0; i < moonDayCount; i++) {
      const d = getLocalNoonDate(i);
      const mTimes = SunCalc.getMoonTimes(d, lat, lon);
      const mDetails = getMoonPhaseDetails(d);
      
      let curMoonSet = mTimes.set;
      if (!curMoonSet && mTimes.rise) {
        const nextDayRef = getLocalNoonDate(i + 1);
        const nextDayMoon = SunCalc.getMoonTimes(nextDayRef, lat, lon);
        if (nextDayMoon.set && (!nextDayMoon.rise || nextDayMoon.set < nextDayMoon.rise)) {
          curMoonSet = nextDayMoon.set;
        }
      }

      moonPhaseOutlook.push({
        date: d.toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric', timeZone: tz }),
        name: mDetails.name,
        icon: mDetails.icon,
        rise: formatTime(mTimes.rise),
        set: formatTime(curMoonSet)
      });
    }

    const selectedSunriseModel = store.getState().selectedModels['sunrise'] || manifest.default_models?.['sunrise'] || 'gw_v2_blend_50';
    const selectedAlpenglowModel = store.getState().selectedModels['alpenglow'] || manifest.default_models?.['alpenglow'] || 'gw_v2_blend_50';

    let sunriseModel = selectedSunriseModel;
    let alpenglowModel = selectedAlpenglowModel;

    if (source !== 'blend' && source !== 'nws' && source !== 'eccc') {
      sunriseModel = source;
      alpenglowModel = source;
    }

    // Solar & Alpenglow details for horizonDayCount (at least 3 days, starting with today in POI timezone)
    if (reqs.needsSolar) {
      const solarDayCount = Math.max(3, horizonDayCount);
      const hasBinarySolar = Boolean(
        pointData && (
          pointData.sunriseScore?.some(v => v > 0) ||
          pointData.sunsetScore?.some(v => v > 0) ||
          pointData.alpenglowMorning?.some(v => v > 0) ||
          pointData.alpenglowEvening?.some(v => v > 0) ||
          pointData.goldenHourMorning?.some(v => v > 0) ||
          pointData.goldenHourEvening?.some(v => v > 0)
        )
      );

      const sunriseFrames = !hasBinarySolar ? ManifestTimelineHelper.getProductFrames(manifest, 'sunrise', sunriseModel) : [];
      const alpenglowFrames = !hasBinarySolar ? ManifestTimelineHelper.getProductFrames(manifest, 'alpenglow', alpenglowModel) : [];
      const goldenHourFrames = !hasBinarySolar ? ManifestTimelineHelper.getProductFrames(manifest, 'goldenhour' as any, sunriseModel) : [];
      const availableSunriseKeys = new Set(sunriseFrames.map(f => `${f.dateStr}_${f.hourStr}`));
      const availableAlpenKeys = new Set(alpenglowFrames.map(f => `${f.dateStr}_${f.hourStr}`));
      const availableGoldenKeys = new Set(goldenHourFrames.map(f => `${f.dateStr}_${f.hourStr}`));

      for (let i = 0; i < solarDayCount; i++) {
        const d = getLocalNoonDate(i);
        const curDateStr = formatLocalYMD(d, tz);
        
        const sTimes = SunCalc.getTimes(d, lat, lon);
        const goldenHourMorn = `${formatTime(sTimes.sunrise)} - ${formatTime(sTimes.goldenHourEnd)}`;
        const goldenHourEve = `${formatTime(sTimes.goldenHour)} - ${formatTime(sTimes.sunset)}`;
        const blueHourMorn = `${formatTime(sTimes.dawn)} - ${formatTime(sTimes.sunrise)}`;
        const blueHourEve = `${formatTime(sTimes.sunset)} - ${formatTime(sTimes.dusk)}`;

        if (hasBinarySolar && pointData) {
          let sunriseScore = 0;
          let sunsetScore = 0;
          let alpenglowMorn = 0;
          let alpenglowEve = 0;
          let goldenHourMornScore: number | undefined = undefined;
          let goldenHourEveScore: number | undefined = undefined;

          const matchingHours: number[] = [];
          for (let h = 0; h < pointData.numHours; h++) {
            const stepDate = new Date((pointData.cycleEpoch + h * 3600) * 1000);
            const stepDateStr = stepDate.toLocaleDateString('en-CA', { timeZone: tz });
            if (stepDateStr === curDateStr) {
              matchingHours.push(h);
            }
          }

          if (matchingHours.length > 0) {
            const repH = matchingHours[Math.floor(matchingHours.length / 2)];
            sunriseScore = pointData.sunriseScore?.[repH] ?? 0;
            sunsetScore = pointData.sunsetScore?.[repH] ?? 0;
            alpenglowMorn = pointData.alpenglowMorning?.[repH] ?? 0;
            alpenglowEve = pointData.alpenglowEvening?.[repH] ?? 0;
            if (pointData.goldenHourMorning) {
              goldenHourMornScore = pointData.goldenHourMorning[repH] ?? 0;
            }
            if (pointData.goldenHourEvening) {
              goldenHourEveScore = pointData.goldenHourEvening[repH] ?? 0;
            }
          }

          solarOutlook.push({
            date: d.toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric', timeZone: tz }),
            sunriseTime: formatTime(sTimes.sunrise),
            sunsetTime: formatTime(sTimes.sunset),
            sunriseScore,
            sunsetScore,
            alpenglowMorn,
            alpenglowEve,
            goldenHourMornScore,
            goldenHourEveScore,
            goldenHourMorn,
            goldenHourEve,
            blueHourMorn,
            blueHourEve
          });
        } else if (!TierAccessService.userHasLayerAccess('sunrise')) {
          solarOutlook.push({
            date: d.toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric', timeZone: tz }),
            sunriseTime: formatTime(sTimes.sunrise),
            sunsetTime: formatTime(sTimes.sunset),
            sunriseScore: 0,
            sunsetScore: 0,
            alpenglowMorn: 0,
            alpenglowEve: 0,
            goldenHourMornScore: undefined,
            goldenHourEveScore: undefined,
            goldenHourMorn,
            goldenHourEve,
            blueHourMorn,
            blueHourEve
          });
        } else {
          const hasSunrise = availableSunriseKeys.has(`${curDateStr}_sunrise`);
          const hasSunset = availableSunriseKeys.has(`${curDateStr}_sunset`);
          const hasAlpenMorn = availableAlpenKeys.has(`${curDateStr}_alpenglow_morning`);
          const hasAlpenEve = availableAlpenKeys.has(`${curDateStr}_alpenglow_evening`);
          const hasGoldenMorn = availableGoldenKeys.has(`${curDateStr}_goldenhour_morning`);
          const hasGoldenEve = availableGoldenKeys.has(`${curDateStr}_goldenhour_evening`);

          const sunriseUrl = hasSunrise ? buildSampleTileUrl(getTileUrl(manifest, 'sunrise', sunriseModel, curDateStr, 'sunrise'), tileCoords) : '';
          const sunsetUrl = hasSunset ? buildSampleTileUrl(getTileUrl(manifest, 'sunrise', sunriseModel, curDateStr, 'sunset'), tileCoords) : '';
          const alpenglowMornUrl = hasAlpenMorn ? buildSampleTileUrl(getTileUrl(manifest, 'alpenglow', alpenglowModel, curDateStr, 'alpenglow_morning'), tileCoords) : '';
          const alpenglowEveUrl = hasAlpenEve ? buildSampleTileUrl(getTileUrl(manifest, 'alpenglow', alpenglowModel, curDateStr, 'alpenglow_evening'), tileCoords) : '';
          const goldenMornUrl = hasGoldenMorn ? buildSampleTileUrl(getTileUrl(manifest, 'goldenhour' as any, sunriseModel, curDateStr, 'goldenhour_morning'), tileCoords) : '';
          const goldenEveUrl = hasGoldenEve ? buildSampleTileUrl(getTileUrl(manifest, 'goldenhour' as any, sunriseModel, curDateStr, 'goldenhour_evening'), tileCoords) : '';

          scorePromises.push(Promise.all([
            hasSunrise ? getGenericScoreAt(lat, lon, sunriseUrl) : Promise.resolve(0),
            hasSunset ? getGenericScoreAt(lat, lon, sunsetUrl) : Promise.resolve(0),
            hasAlpenMorn ? getGenericScoreAt(lat, lon, alpenglowMornUrl) : Promise.resolve(0),
            hasAlpenEve ? getGenericScoreAt(lat, lon, alpenglowEveUrl) : Promise.resolve(0),
            hasGoldenMorn ? getGenericScoreAt(lat, lon, goldenMornUrl) : Promise.resolve(undefined),
            hasGoldenEve ? getGenericScoreAt(lat, lon, goldenEveUrl) : Promise.resolve(undefined)
          ]).then(([sunriseScore, sunsetScore, alpenglowMorn, alpenglowEve, goldenHourMornScore, goldenHourEveScore]) => {
            return {
              date: d.toLocaleDateString([], { weekday: 'short', month: 'numeric', day: 'numeric', timeZone: tz }),
              sunriseTime: formatTime(sTimes.sunrise),
              sunsetTime: formatTime(sTimes.sunset),
              sunriseScore,
              sunsetScore,
              alpenglowMorn,
              alpenglowEve,
              goldenHourMornScore,
              goldenHourEveScore,
              goldenHourMorn,
              goldenHourEve,
              blueHourMorn,
              blueHourEve
            };
          }));
        }
      }

      if (scorePromises.length > 0) {
        const resolvedSolar = await Promise.all(scorePromises);
        solarOutlook.push(...resolvedSolar);
      }
    }

    // Format local date, time, and timezone abbreviation for the header display
    const monthName = targetDate.toLocaleDateString('en-US', { timeZone: tz, month: 'long' });
    const dayVal = targetDate.toLocaleDateString('en-US', { timeZone: tz, day: 'numeric' });
    const yearVal = targetDate.toLocaleDateString('en-US', { timeZone: tz, year: 'numeric' });
    const localDate = `${monthName}, ${dayVal} ${yearVal}`;

    const localTimeRaw = targetDate.toLocaleTimeString('en-US', { timeZone: tz, hour: 'numeric', minute: '2-digit' });
    const localTime = isMax ? `${localTimeRaw} (Live Extent)` : localTimeRaw;
    const tzNameFormatter = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      timeZoneName: 'short'
    });
    const tzParts = tzNameFormatter.formatToParts(targetDate);
    const timezoneAbbr = tzParts.find(p => p.type === 'timeZoneName')?.value || tz;

    let recentObserved: RecentObservedData | null = null;
    if (reqs.needsObserved) {
      try {
        recentObserved = await ObservedBinaryService.getObservedForPoint(lat, lon, manifest);
      } catch (e) {
        console.warn('[PoiDataService] Error loading observed binary point data:', e);
      }
    }

    return {
      timeLabels,
      timelineStartMs,
      hasBinaryPointData: Boolean(pointData),
      accessDenial: activeAccessDenial,
      darkskiesScores,
      dewRiskHourly,
      seeingScores,
      seeingArcsec,
      tempsFlat,
      tempsMountain,
      tempsMid,
      tempsForecast,
      tempsHighest,
      tempsLowest,
      tempsPoi,
      precipLikelihood,
      precipRate,
      snowRate,
      cloudCover,
      smokeDensity,
      aqiValues,
      windSpeed,
      windDir,
      lightningData: null,
      thunderstormProb,
      recentObserved,
      
      poiElev,
      baseElev,
      forecastElev,
      minElev,
      maxElev,
      avgFreezingElev,
      freezingLevels: freezingElevs,

      astroDawnTime,
      astroDuskTime,
      darkskiesScore,
      
      moonIcon,
      moonName,
      moonIllumination,
      moonRiseTime,
      moonSetTime,

      // Sun Intensity (Solar Irradiance W/m²) calculations aligned to actual timelineStartMs timestamps
      ...(() => {
        const hasBinarySolarFlux = Boolean(
          pointData && Array.isArray(pointData.sunIntensityWm2) && pointData.sunIntensityWm2.some(v => v > 0)
        );
        const sunIntensityHourly = timeLabels.map((_, i) => {
          const sIdx = hourlyResults[i]?.stepIdx;
          if (hasBinarySolarFlux && pointData && sIdx !== null && sIdx !== undefined && pointData.sunIntensityWm2[sIdx] !== undefined) {
            return Math.max(0, Math.round(pointData.sunIntensityWm2[sIdx]));
          }
          const stepDate = new Date(timelineStartMs + i * 3600 * 1000);
          const sPos = SunCalc.getPosition(stepDate, lat, lon);
          if (!sPos || typeof sPos.altitude !== 'number' || sPos.altitude <= 0) return 0;
          const clearSkyWm2 = 1050.0 * Math.sin((sPos.altitude * Math.PI) / 180.0);
          const cloudAtten = 1.0 - 0.75 * Math.pow((cloudCover[i] || 0) / 100, 1.5);
          return Math.round(Math.max(0, clearSkyWm2 * cloudAtten));
        });
        const maxSunIntensity = Math.max(...sunIntensityHourly, 0);
        let sunIntensityCategory = 'Dim / Twilight';
        if (maxSunIntensity >= 1100) sunIntensityCategory = 'Extreme';
        else if (maxSunIntensity >= 900) sunIntensityCategory = 'Intense';
        else if (maxSunIntensity >= 650) sunIntensityCategory = 'Strong';
        else if (maxSunIntensity >= 350) sunIntensityCategory = 'Moderate';
        else if (maxSunIntensity >= 150) sunIntensityCategory = 'Low';

        return { maxSunIntensity, sunIntensityCategory, sunIntensityHourly };
      })(),

      moonPhaseOutlook,
      solarOutlook,
      darkskiesTrends,
      spaceWeather: spaceWeatherTelemetry,

      timezone: tz,
      localDate,
      localTime,
      timezoneAbbr,

      opticsDewRisk: pointData && pointData.dewRiskScore.length > 0 ? (() => {
        const curDewScore = dewRiskHourly.length > 0 ? dewRiskHourly[0] : pointData.dewRiskScore[0];
        return {
          level: curDewScore >= 70 ? 'high' : (curDewScore >= 40 ? 'moderate' : 'low'),
          badge: curDewScore >= 70 ? '🔴 HIGH DEW RISK' : (curDewScore >= 40 ? '🟠 MODERATE DEW RISK' : '🟢 LOW DEW RISK'),
          score: curDewScore
        };
      })() : (TierAccessService.userHasLayerAccess('optics_dew_risk') ? await getOpticsDewRiskStatus(lat, lon, now) : { level: 'low', badge: '🟢 LOW DEW RISK', score: 0 }),

      soundings: soundingColumns,
      inversionMetrics: inversionMetricsList
    };
  }
}

const opticsRiskCache = new Map<string, { level: string; badge: string; score: number }>();

export async function getOpticsDewRiskStatus(lat: number, lon: number, atDate?: Date): Promise<{level: string, badge: string, score: number}> {
  const state = store.getState();
  const manifest = state.manifest;
  const current = atDate || state.currentTimeStep;
  if (!manifest || !current) return { level: 'low', badge: '🟢 LOW DEW RISK', score: 0 };
  
  const dateStr = current.toISOString().split('T')[0];
  const targetHour = String(current.getUTCHours()).padStart(2, '0');
  const cacheKey = `${lat.toFixed(3)}_${lon.toFixed(3)}_${dateStr}_${targetHour}`;
  if (opticsRiskCache.has(cacheKey)) {
    return opticsRiskCache.get(cacheKey)!;
  }
  
  // Ensemble multi-model candidates: evaluate high-res regional models first, then global models
  const candidateModels = ['hrrr', 'rrfs', 'rdps', 'ecmwf', 'gfs'];
  const promises = candidateModels.map(async (m) => {
    const avail = manifest.optics_dew_risk?.available_by_model?.[m]?.[dateStr];
    if (!avail || avail.length === 0) return null;
    let hourStr = targetHour;
    if (!avail.includes(hourStr)) {
      hourStr = avail[avail.length - 1];
    }
    try {
      const tileUrl = getTileUrl(manifest, 'optics_dew_risk', m, dateStr, hourStr);
      if (!tileUrl) return null;
      const pixel = await getPixelAt(lat, lon, tileUrl);
      if (!pixel || pixel[3] === 0) return null;
      
      const r = pixel[0];
      const g = pixel[1];
      let score = 0;
      let level = 'low';
      if (r > 180 && g < 100) {
        score = 90;
        level = 'heavy';
      } else if (r > 180 && g >= 100) {
        score = 50;
        level = 'moderate';
      } else if (g > 150) {
        score = 10;
        level = 'low';
      }
      return { model: m, score, level };
    } catch (_e) {
      return null;
    }
  });

  const results = (await Promise.all(promises)).filter((r): r is NonNullable<typeof r> => r !== null);
  let finalStatus: { level: string; badge: string; score: number };
  if (results.length === 0) {
    finalStatus = { level: 'low', badge: '🟢 LOW DEW RISK', score: 0 };
  } else {
    // Take the highest risk score across available models to protect lenses & telescopes
    const maxResult = results.reduce((prev, curr) => (curr.score > prev.score ? curr : prev), results[0]);
    if (maxResult.score >= 70) {
      finalStatus = { level: 'heavy', badge: '🔴 DEW HEATER REQUIRED', score: maxResult.score };
    } else if (maxResult.score >= 35) {
      finalStatus = { level: 'moderate', badge: '🟡 MODERATE DEW RISK', score: maxResult.score };
    } else {
      finalStatus = { level: 'low', badge: '🟢 LOW DEW RISK', score: maxResult.score };
    }
  }

  opticsRiskCache.set(cacheKey, finalStatus);
  return finalStatus;
}

export interface HrtempTrendPoint {
  dateStr: string;
  hourStr: string;
  timestamp: number;
  localLabel: string;
  temp: number | null;
}

export interface HrtempTrendResult {
  labels: string[];
  temps: (number | null)[];
  points: HrtempTrendPoint[];
  units: 'imperial' | 'metric';
}

export async function fetchHrtempTrend(
  lat: number,
  lon: number,
  _elev: number,
  model: string,
  manifest: Manifest,
  stepHours: number = 3
): Promise<HrtempTrendResult> {
  const units = store.getState().units;
  const isImperial = units === 'imperial';
  const tz = tzlookup(lat, lon) || store.getState().userTimezone || 'UTC';

  // Primary: Fast spatial binary lookup
  try {
    const forecast = await ForecastBinaryService.getForecastForPoint(lat, lon, manifest, model);
    if (forecast && forecast.numHours > 0) {
      const stepMs = Math.max(1, stepHours) * 3600 * 1000;
      const points: HrtempTrendPoint[] = [];
      const tempsArray = isImperial ? forecast.tempsF : forecast.tempsC;

      const rawOffsets = forecast.rawHourOffsets || ForecastBinaryService.getRawHourOffsetsForModel(model, forecast.numHours);
      let lastTs = -Infinity;
      for (let i = 0; i < forecast.numHours; i++) {
        const hourOffset = rawOffsets[i] ?? i;

        const ts = (forecast.cycleEpoch + hourOffset * 3600) * 1000;
        if (ts - lastTs >= stepMs - 60000) {
          lastTs = ts;
          const d = new Date(ts);
          const weekdayStr = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: tz });
          const hourStr = d.toLocaleTimeString('en-US', { hour: 'numeric', hour12: isImperial, timeZone: tz }).replace(' ', '');
          const localLabel = `${weekdayStr} ${hourStr}`;
          const dateStr = d.toISOString().split('T')[0];
          const hourPadded = String(d.getUTCHours()).padStart(2, '0');

          points.push({
            dateStr,
            hourStr: `Z${hourPadded}`,
            timestamp: ts,
            localLabel,
            temp: tempsArray[i] ?? null
          });
        }
      }

      if (points.length > 0) {
        return {
          labels: points.map(p => p.localLabel),
          temps: points.map(p => p.temp),
          points,
          units
        };
      }
    }
  } catch (err) {
    console.warn('Binary forecast lookup failed in fetchHrtempTrend, falling back to pmtiles:', err);
  }

  if (ForecastBinaryService.lastAccessDenial || !TierAccessService.userHasLayerAccess('hrtemp')) {
    return { labels: [], temps: [], points: [], units };
  }

  const hrtempConfig = manifest.hrtemp?.available_by_model || manifest.hrtemp?.available || {};
  let targetModel = model;
  let modelData = hrtempConfig[targetModel];

  if (!modelData || Object.keys(modelData).length === 0) {
    targetModel = 'blend';
    modelData = hrtempConfig['blend'];
  }
  if (!modelData || Object.keys(modelData).length === 0) {
    const firstKey = Object.keys(hrtempConfig)[0];
    if (firstKey) {
      targetModel = firstKey;
      modelData = hrtempConfig[firstKey];
    }
  }

  if (!modelData || Object.keys(modelData).length === 0) {
    return { labels: [], temps: [], points: [], units: store.getState().units };
  }

  const allPoints: { dateStr: string; hourStr: string; timestamp: number }[] = [];
  for (const d of Object.keys(modelData).sort()) {
    const hours = modelData[d] || [];
    for (const h of hours) {
      const hClean = h.replace(/^Z/, '');
      const hNum = parseInt(hClean.replace(/\D/g, ''), 10);
      if (isNaN(hNum)) continue;
      const hPadded = String(hNum).padStart(2, '0');
      const ts = new Date(`${d}T${hPadded}:00:00Z`).getTime();
      allPoints.push({ dateStr: d, hourStr: h, timestamp: ts });
    }
  }
  allPoints.sort((a, b) => a.timestamp - b.timestamp);

  const now = new Date();
  const nowRoundedMs = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), now.getUTCHours(), 0, 0, 0);

  let futurePoints = allPoints.filter(p => p.timestamp >= nowRoundedMs);
  if (futurePoints.length === 0) {
    // Fallback for test fixture environments where system date is past the data range
    futurePoints = allPoints;
  }

  const stepMs = Math.max(1, stepHours) * 3600 * 1000;
  const sampledPoints: typeof futurePoints = [];
  let lastTs = -Infinity;
  for (let i = 0; i < futurePoints.length; i++) {
    const pt = futurePoints[i];
    if (pt.timestamp - lastTs >= stepMs - 60000) {
      sampledPoints.push(pt);
      lastTs = pt.timestamp;
    }
  }

  const probeZoom = 4;
  const tx = lng2tile(lon, probeZoom);
  const ty = lat2tile(lat, probeZoom);
  const tileCoords = `${probeZoom}/${tx}/${ty}`;

  const promises = sampledPoints.map(async (pt) => {
    try {
      const url = getTileUrl(manifest, 'hrtemp', targetModel, pt.dateStr, pt.hourStr);
      const sampleUrl = buildSampleTileUrl(url, tileCoords);
      const pixel = await getPixelAt(lat, lon, sampleUrl);
      let temp: number | null = null;
      if (pixel && pixel[3] > 0) {
        const tempC = decodeTemp(pixel[0], pixel[1], pixel[2]);
        temp = isImperial ? Math.round(tempC * 1.8 + 32) : Math.round(tempC);
      }

      const d = new Date(pt.timestamp);
      const weekdayStr = d.toLocaleDateString('en-US', { weekday: 'short', timeZone: tz });
      const hourStr = d.toLocaleTimeString('en-US', { hour: 'numeric', hour12: isImperial, timeZone: tz }).replace(' ', '');
      const localLabel = `${weekdayStr} ${hourStr}`;

      return {
        dateStr: pt.dateStr,
        hourStr: pt.hourStr,
        timestamp: pt.timestamp,
        localLabel,
        temp
      };
    } catch (_e) {
      return {
        dateStr: pt.dateStr,
        hourStr: pt.hourStr,
        timestamp: pt.timestamp,
        localLabel: '',
        temp: null
      };
    }
  });

  const results = await Promise.all(promises);
  return {
    labels: results.map(r => r.localLabel),
    temps: results.map(r => r.temp),
    points: results,
    units
  };
}

export interface LightningTrendPoint {
  hour: number;
  hourLabel: string;
  timestamp: number;
  level: number; // 0: None, 1: Low, 2: Moderate, 3: High
  levelLabel: string;
}

export interface LightningTrendResult {
  dateStr: string;
  points: LightningTrendPoint[];
  labels: string[];
  levels: number[];
  maxLevel: number;
}

const lightningGeoJsonCache = new Map<string, any>();

export function pointInPolygonRing(pt: [number, number], ring: number[][]): boolean {
  const x = pt[0];
  const y = pt[1];
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1];
    const xj = ring[j][0], yj = ring[j][1];
    const intersect = ((yi > y) !== (yj > y)) && (x < ((xj - xi) * (y - yi)) / (yj - yi) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

export function sampleLightningLevelAt(lon: number, lat: number, geojson: any): number {
  if (!geojson || !geojson.features || !Array.isArray(geojson.features)) return 0;
  let maxLevel = 0;
  const pt: [number, number] = [lon, lat];

  for (const feature of geojson.features) {
    if (!feature || !feature.geometry) continue;
    const geom = feature.geometry;
    const level = feature.properties?.level || 0;
    if (level <= maxLevel) continue;

    if (geom.type === 'Polygon') {
      const rings = geom.coordinates;
      if (rings && rings.length > 0) {
        if (pointInPolygonRing(pt, rings[0])) {
          let inHole = false;
          for (let h = 1; h < rings.length; h++) {
            if (pointInPolygonRing(pt, rings[h])) {
              inHole = true;
              break;
            }
          }
          if (!inHole) {
            maxLevel = Math.max(maxLevel, level);
          }
        }
      }
    } else if (geom.type === 'MultiPolygon') {
      const polys = geom.coordinates;
      if (polys) {
        for (const rings of polys) {
          if (rings && rings.length > 0) {
            if (pointInPolygonRing(pt, rings[0])) {
              let inHole = false;
              for (let h = 1; h < rings.length; h++) {
                if (pointInPolygonRing(pt, rings[h])) {
                  inHole = true;
                  break;
                }
              }
              if (!inHole) {
                maxLevel = Math.max(maxLevel, level);
                break;
              }
            }
          }
        }
      }
    }
  }
  return maxLevel;
}

export async function fetchLightningTrend(
  lat: number,
  lon: number,
  dateStr: string,
  manifest: Manifest,
  model: string = 'blend',
  rolling24h: boolean = false
): Promise<LightningTrendResult> {
  const units = store.getState().units;
  const isImperial = units === 'imperial';
  const levelLabels = ['None', 'Low Risk', 'Moderate Risk', 'High Risk'];

  let tz = 'UTC';
  try {
    tz = tzlookup(lat, lon);
  } catch {
    tz = 'UTC';
  }

  const now = new Date();
  const currentHourFloorMs = Math.floor(now.getTime() / (3600 * 1000)) * 3600 * 1000;
  const nowParts = getLocalDateTimeParts(now.getTime(), tz);
  const isToday = dateStr === nowParts.ymd;

  const formatHourLabel = (localHour: number, weekdayPrefix?: string): string => {
    const hourPadded = String(localHour).padStart(2, '0');
    const base = isImperial
      ? (localHour === 0 ? '12 AM' : localHour < 12 ? `${localHour} AM` : localHour === 12 ? '12 PM' : `${localHour - 12} PM`)
      : `${hourPadded}:00`;
    return weekdayPrefix ? `${weekdayPrefix} ${base}` : base;
  };

  const targetSpecs: Array<{ orderIdx: number; localHour: number; hourLabel: string; targetMs: number }> = [];
  if (isToday && rolling24h) {
    for (let step = 0; step < 24; step++) {
      const targetMs = currentHourFloorMs + step * 3600 * 1000;
      const lp = getLocalDateTimeParts(targetMs, tz);
      targetSpecs.push({
        orderIdx: step,
        localHour: lp.hour,
        hourLabel: formatHourLabel(lp.hour, lp.weekdayShort),
        targetMs
      });
    }
  } else {
    const [y, m, d] = dateStr.split('-').map(Number);
    const startHour = isToday ? nowParts.hour : 0;
    for (let localHour = startHour; localHour < 24; localHour++) {
      const targetMs = localDateTimeToUtcMs(y, m, d, localHour, 0, tz);
      targetSpecs.push({
        orderIdx: localHour,
        localHour,
        hourLabel: formatHourLabel(localHour),
        targetMs
      });
    }
  }

  // Primary: Fast spatial binary lookup
  try {
    const forecast = await ForecastBinaryService.getForecastForPoint(lat, lon, manifest, model);
    if (forecast && forecast.numHours > 0) {
      const rawOffsets = forecast.rawHourOffsets || ForecastBinaryService.getRawHourOffsetsForModel(model, forecast.numHours);
      const points: LightningTrendPoint[] = targetSpecs.map(spec => {
        let closestIdx = -1;
        let minDiff = Infinity;
        for (let i = 0; i < forecast.numHours; i++) {
          const hourOffset = rawOffsets[i] ?? i;
          const ts = (forecast.cycleEpoch + hourOffset * 3600) * 1000;
          const diff = Math.abs(ts - spec.targetMs);
          if (diff < minDiff) {
            minDiff = diff;
            closestIdx = i;
          }
        }

        let level = 0;
        if (closestIdx !== -1 && minDiff < 3 * 3600 * 1000) {
          const score = forecast.lightningHazard[closestIdx] ?? 0;
          if (score >= 70) level = 3;
          else if (score >= 35) level = 2;
          else if (score >= 10) level = 1;
        }

        return {
          hour: spec.localHour,
          hourLabel: spec.hourLabel,
          timestamp: spec.targetMs,
          level,
          levelLabel: levelLabels[level] || 'None'
        };
      });

      const maxLevel = points.reduce((max, pt) => Math.max(max, pt.level), 0);
      return {
        dateStr,
        points,
        labels: points.map(r => r.hourLabel),
        levels: points.map(r => r.level),
        maxLevel
      };
    }
  } catch (err) {
    console.warn('Binary forecast lookup failed in fetchLightningTrend, falling back to GeoJSON:', err);
  }

  if (ForecastBinaryService.lastAccessDenial || !TierAccessService.userHasLayerAccess('lightning')) {
    return {
      dateStr,
      points: [],
      labels: [],
      levels: [],
      maxLevel: 0
    };
  }

  const apiServer = getApiServer(manifest);
  const tileRoot = (manifest ? manifest.tile_base : "tiles").replace(/^\/+|\/+$/g, '');
  const cleanServer = apiServer.replace(/\/$/, '');

  const hourPromises = targetSpecs.map(async (spec) => {
    const utcDate = new Date(spec.targetMs);
    const utcDateStr = utcDate.toISOString().split('T')[0];
    const utcHourStr = String(utcDate.getUTCHours()).padStart(2, '0');

    const jsonFileName = (model === 'blend' || !model)
      ? `lightning_${utcDateStr}_Z${utcHourStr}.json`
      : `lightning_${model}_${utcDateStr}_Z${utcHourStr}.json`;

    let cleanPath = `${tileRoot}/forecast/lightning/lightning_forecast/${jsonFileName}`.replace(/\/+/g, '/');
    if (cleanPath.startsWith('/')) {
      cleanPath = cleanPath.substring(1);
    }
    const url = `${cleanServer}/${cleanPath}`;

    let geojson = lightningGeoJsonCache.get(url);
    if (!geojson) {
      try {
        let res = await fetch(url);
        if (!res.ok && (model === 'blend' || !model)) {
          const altName = `lightning_blend_${utcDateStr}_Z${utcHourStr}.json`;
          let altPath = `${tileRoot}/forecast/lightning/lightning_forecast/${altName}`.replace(/\/+/g, '/');
          if (altPath.startsWith('/')) altPath = altPath.substring(1);
          res = await fetch(`${cleanServer}/${altPath}`);
        }
        if (res.ok) {
          geojson = await res.json();
          lightningGeoJsonCache.set(url, geojson);
        }
      } catch (_e) {
        // Not found or network error
      }
    }

    const level = geojson ? sampleLightningLevelAt(lon, lat, geojson) : 0;

    return {
      orderIdx: spec.orderIdx,
      point: {
        hour: spec.localHour,
        hourLabel: spec.hourLabel,
        timestamp: spec.targetMs,
        level,
        levelLabel: levelLabels[level] || 'None'
      }
    };
  });

  const rawResults = await Promise.all(hourPromises);
  rawResults.sort((a, b) => a.orderIdx - b.orderIdx);
  const results = rawResults.map(r => r.point);

  const maxLevel = results.reduce((max, pt) => Math.max(max, pt.level), 0);

  return {
    dateStr,
    points: results,
    labels: results.map(r => r.hourLabel),
    levels: results.map(r => r.level),
    maxLevel
  };
}
